/* ============================================================================
   Beeglee / Nisho — custom theme features for Prestige 11.x
   Adds: dynamic cart cross-sell, localStorage wishlist (button + count + panel),
         cart<->wishlist drawer tabs.
   Size pills (E) and collection sub-nav (D) are pure Liquid/CSS — no JS needed.
   ========================================================================== */
(() => {
  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  const HEART_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';

  /* ---------- shared money formatter (cents -> localized currency) -------- */
  const formatMoney = (cents) => {
    const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };

  /* ======================================================================
     WISHLIST STORE
     - localStorage (guest + device)
     - cart attributes suhi_wishlist (server cart → carries to account when logged in)
     - customer-scoped localStorage key after login (same browser, multi-account safe)
     ====================================================================== */
  const WL_KEY = 'theme:wishlist';
  const WL_CART_ATTR = 'suhi_wishlist';
  const customerId = () => {
    try {
      return (window.__SUHI_CUSTOMER && window.__SUHI_CUSTOMER.loggedIn && window.__SUHI_CUSTOMER.id) || null;
    } catch (e) {
      return null;
    }
  };
  const wlCustomerKey = () => {
    const id = customerId();
    return id ? `theme:wishlist:c${id}` : null;
  };
  const wlParse = (raw) => {
    if (!raw) return [];
    try {
      const v = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!Array.isArray(v)) return [];
      return v
        .map((i) => {
          if (!i) return null;
          if (typeof i === 'string') return { handle: i, variantId: null, addedAt: Date.now() };
          if (i.handle) return { handle: i.handle, variantId: i.variantId || null, addedAt: i.addedAt || Date.now() };
          return null;
        })
        .filter(Boolean);
    } catch (e) {
      return [];
    }
  };
  const wlMerge = (...lists) => {
    const map = new Map();
    lists.flat().forEach((item) => {
      if (!item || !item.handle) return;
      const prev = map.get(item.handle);
      if (!prev || (item.addedAt || 0) >= (prev.addedAt || 0)) {
        map.set(item.handle, {
          handle: item.handle,
          variantId: item.variantId || (prev && prev.variantId) || null,
          addedAt: item.addedAt || (prev && prev.addedAt) || Date.now()
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
  };
  const wlReadLocal = () => {
    const guest = wlParse(localStorage.getItem(WL_KEY));
    const ck = wlCustomerKey();
    const scoped = ck ? wlParse(localStorage.getItem(ck)) : [];
    return wlMerge(guest, scoped);
  };
  const wlRead = () => wlReadLocal();

  let _wlPushTimer = 0;
  const wlPushToCart = (arr) => {
    /* Debounce cart attribute writes */
    clearTimeout(_wlPushTimer);
    _wlPushTimer = setTimeout(async () => {
      try {
        const payload = JSON.stringify(
          (arr || []).map((i) => ({
            handle: i.handle,
            variantId: i.variantId || null,
            addedAt: i.addedAt || Date.now()
          }))
        );
        await fetch(`${root()}cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { [WL_CART_ATTR]: payload } })
        });
      } catch (e) {
        /* offline / blocked — localStorage still holds data */
      }
    }, 280);
  };

  const wlWrite = (arr, { skipCart } = {}) => {
    const list = Array.isArray(arr) ? arr : [];
    try {
      localStorage.setItem(WL_KEY, JSON.stringify(list));
      const ck = wlCustomerKey();
      if (ck) localStorage.setItem(ck, JSON.stringify(list));
    } catch (e) {}
    if (!skipCart) wlPushToCart(list);
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items: list } }));
  };
  const wlHas = (handle) => wlRead().some((i) => i.handle === handle);
  const wlToggle = (handle, variantId) => {
    if (!handle) return false;
    const arr = wlRead();
    const idx = arr.findIndex((i) => i.handle === handle);
    if (idx >= 0) arr.splice(idx, 1);
    else arr.push({ handle, variantId: variantId || null, addedAt: Date.now() });
    wlWrite(arr);
    return wlHas(handle);
  };

  /* Merge guest likes + cart attributes + customer-scoped storage (login carry-over) */
  const wlBootstrapAccountSync = async () => {
    try {
      const guest = wlParse(localStorage.getItem(WL_KEY));
      const ck = wlCustomerKey();
      const scoped = ck ? wlParse(localStorage.getItem(ck)) : [];
      let fromCart = wlParse(window.__SUHI_CART_ATTR_WISHLIST || '');
      try {
        const cart = await fetch(`${root()}cart.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        }).then((r) => r.json());
        const raw =
          (cart.attributes && (cart.attributes[WL_CART_ATTR] || cart.attributes.suhi_wishlist)) || '';
        if (raw) fromCart = wlMerge(fromCart, wlParse(raw));
      } catch (e) {}

      const merged = wlMerge(guest, scoped, fromCart);
      /* Always write merged set so hearts/count refresh after login */
      wlWrite(merged, { skipCart: false });

      /* Mark last login customer so we re-merge when account switches */
      try {
        const id = customerId();
        if (id) localStorage.setItem('theme:wishlist:lastCustomer', String(id));
        else localStorage.removeItem('theme:wishlist:lastCustomer');
      } catch (e) {}
    } catch (e) {}
  };

  /* Cart refresh after login / back-forward cache so account cart appears in drawer.
     IMPORTANT: do NOT fire cart:change with a bare /cart.js payload — Prestige
     CartDrawer.onCartChange expects cart.sections[sectionId] HTML. Missing
     sections throws inside replaceContent and can leave the drawer closing itself. */
  const refreshCartFromServer = async () => {
    try {
      const cart = await fetch(`${root()}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then((r) => r.json());
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return cart;
    } catch (e) {
      return null;
    }
  };

  const bootIdentitySync = () => {
    wlBootstrapAccountSync();
    /* Logged-in customers: force cart pull so drawer matches account cart */
    if (customerId()) {
      refreshCartFromServer();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootIdentitySync);
  } else {
    bootIdentitySync();
  }
  /* Login return / bfcache: re-sync */
  window.addEventListener('pageshow', (e) => {
    if (e.persisted || customerId()) {
      wlBootstrapAccountSync();
      refreshCartFromServer();
    }
  });
  document.addEventListener('shopify:section:load', () => {
    wlBootstrapAccountSync();
  });

  /* ---------- <wishlist-button> : heart toggle on cards / PDP ----------- */
  class WishlistButton extends HTMLElement {
    connectedCallback() {
      /* Re-bind every connect (popover move-to-body disconnects/reconnects) */
      this.handle = this.getAttribute('handle') || this.handle || '';
      this.variantId = this.getAttribute('variant-id') || this.variantId || '';
      if (this._click) this.removeEventListener('click', this._click, true);
      this._click = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        /* Always re-read attrs — shop-the-look / AJAX cards can remount */
        this.handle = this.getAttribute('handle') || this.handle || '';
        this.variantId = this.getAttribute('variant-id') || this.variantId || '';
        if (!this.handle) return;
        wlToggle(this.handle, this.variantId);
        this.reflect();
      };
      this._sync = () => this.reflect();
      /* Capture so carousel / popover drag handlers don't swallow the tap */
      this.addEventListener('click', this._click, true);
      document.addEventListener('wishlist:change', this._sync);
      this.reflect();
    }
    disconnectedCallback() {
      if (this._click) this.removeEventListener('click', this._click, true);
      document.removeEventListener('wishlist:change', this._sync);
    }
    reflect() {
      this.handle = this.getAttribute('handle') || this.handle || '';
      /* No handle = never show filled (prevents random pre-filled hearts) */
      const on = !!(this.handle && wlHas(this.handle));
      this.classList.toggle('is-active', on);
      const btn = this.querySelector('button') || this;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Add to wishlist');
    }
  }
  if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);

  /* ---------- <wishlist-count> : header dot (no number) --------------- */
  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this._u = () => this.update();
      document.addEventListener('wishlist:change', this._u);
      this.update();
    }
    disconnectedCallback() { document.removeEventListener('wishlist:change', this._u); }
    update() {
      const n = wlRead().length;
      /* Dot only — no filled heart / is-active styling */
      this.textContent = '';
      this.setAttribute('aria-label', n > 0 ? `${n} wishlist items` : 'Wishlist empty');
      this.toggleAttribute('hidden', n === 0);
    }
  }
  if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);

  /* ---------- helpers: cart section refresh after custom cart ops ------- */
  const prepareSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  };

  const dispatchCartUpdate = (cart) => {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'line-item:change', cart }
      })
    );
  };

  const fetchCartJson = async () => {
    const res = await fetch(`${root()}cart.js`);
    return res.json();
  };

  /* ---------- <wishlist-panel> : size select + ATC, no heart icon ------- */
  class WishlistPanel extends HTMLElement {
    connectedCallback() {
      this.listEl = this.querySelector('[data-wishlist-list]');
      this.emptyEl = this.querySelector('[data-wishlist-empty]');
      this._dirty = true;
      this._products = Object.create(null);
      this._onChange = () => {
        this._dirty = true;
        /* If panel is open and we already optimistically updated DOM, skip full re-render */
        if (this.closest('[hidden]')) return;
        if (this._skipNextRender) {
          this._skipNextRender = false;
          this._dirty = true; /* still dirty for next full open */
          return;
        }
        this.render();
      };
      this._onClick = (e) => this.onClick(e);
      this._onSelect = (e) => this.onSelect(e);
      document.addEventListener('wishlist:change', this._onChange);
      this.addEventListener('click', this._onClick);
      this.addEventListener('change', this._onSelect);
    }
    disconnectedCallback() {
      document.removeEventListener('wishlist:change', this._onChange);
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('change', this._onSelect);
    }

    async render() {
      if (!this._dirty || !this.listEl) return;
      this._dirty = false;
      const items = wlRead();
      if (this.emptyEl) this.emptyEl.toggleAttribute('hidden', items.length > 0);
      if (!items.length) {
        this.listEl.innerHTML = '';
        this._products = Object.create(null);
        return;
      }

      /* Keep current cards visible while fetching — avoids white flash */
      this.listEl.classList.add('is-updating');
      this.listEl.setAttribute('aria-busy', 'true');

      const results = await Promise.all(items.map(async (it) => {
        try {
          /* Prefer cached product data when already loaded (remove / re-open) */
          const cached = this._products && this._products[it.handle] && this._products[it.handle]._raw;
          if (cached) return { product: cached, saved: it };
          const res = await fetch(`${root()}products/${it.handle}.js`);
          if (!res.ok) return null;
          return { product: await res.json(), saved: it };
        } catch (e) { return null; }
      }));

      /* Build HTML off-DOM, then swap once */
      const frag = document.createDocumentFragment();
      const nextProducts = Object.create(null);
      const wrap = document.createElement('div');
      results.forEach((r) => {
        if (!r) return;
        nextProducts[r.product.handle] = {
          handle: r.product.handle,
          url: r.product.url,
          price: r.product.price,
          _raw: r.product,
          variants: (r.product.variants || []).map((v) => ({
            id: v.id,
            title: v.title,
            available: v.available,
            price: v.price
          }))
        };
        wrap.insertAdjacentHTML('beforeend', this.cardHTML(r.product, r.saved));
      });
      while (wrap.firstChild) frag.appendChild(wrap.firstChild);

      this._products = nextProducts;
      this.listEl.replaceChildren(frag);
      this.listEl.classList.remove('is-updating');
      this.listEl.removeAttribute('aria-busy');
    }

    /** Instant remove without blanking the whole list */
    removeCard(handle) {
      if (!handle || !this.listEl) return;
      let card = null;
      this.listEl.querySelectorAll('[data-wl-card]').forEach((el) => {
        if (el.getAttribute('data-handle') === handle) card = el;
      });
      if (card) {
        card.style.transition = 'opacity .15s ease, transform .15s ease';
        card.style.opacity = '0';
        card.style.transform = 'translateX(8px)';
        setTimeout(() => {
          card.remove();
          const left = wlRead().length;
          if (this.emptyEl) this.emptyEl.toggleAttribute('hidden', left > 0);
          if (left === 0) this.listEl.innerHTML = '';
        }, 150);
      }
      if (this._products && this._products[handle]) delete this._products[handle];
    }

    pickVariant(p, savedVariantId, selectEl) {
      if (selectEl && selectEl.value) {
        return p.variants.find((v) => String(v.id) === String(selectEl.value)) || null;
      }
      const stored = savedVariantId && p.variants.find((v) => String(v.id) === String(savedVariantId));
      return stored || p.variants.find((v) => v.available) || p.variants[0] || null;
    }

    cardHTML(p, saved) {
      /* Diris / cart-style card: image | title · size · price · actions + Remove */
      const img = (p.featured_image || (p.images && p.images[0]) || '');
      const imgSrc = img
        ? img.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2')
        : '';
      const imgTag = imgSrc
        ? `<img src="${imgSrc}" alt="${(p.title || '').replace(/"/g, '')}" loading="lazy" class="wishlist-card__media" width="88" height="110">`
        : '';

      const variant = this.pickVariant(p, saved.variantId, null);
      const multi = p.variants && p.variants.length > 1;
      const anyAvailable = (p.variants || []).some((v) => v.available);
      const sizeLabel = variant && variant.title && variant.title !== 'Default Title'
        ? variant.title
        : '';

      let sizeBlock = '';
      if (multi) {
        const opts = p.variants.map((v) => {
          const selected = variant && String(v.id) === String(variant.id) ? ' selected' : '';
          const disabled = v.available ? '' : ' disabled';
          const label = `${v.title || 'Default'}${v.available ? '' : ' — Sold out'}`;
          return `<option value="${v.id}"${selected}${disabled}>${label}</option>`;
        }).join('');
        sizeBlock = `
          <p class="wishlist-card__size-text">Size: ${sizeLabel || '—'}</p>
          <label class="wishlist-card__size wishlist-card__size--compact">
            <span class="sr-only">Change size</span>
            <select class="wishlist-card__size-select" data-wl-size data-handle="${p.handle}" aria-label="Select size for ${(p.title || '').replace(/"/g, '')}">
              ${opts}
            </select>
          </label>`;
      } else if (sizeLabel) {
        sizeBlock = `<p class="wishlist-card__size-text">Size: ${sizeLabel}</p>`;
      }

      let action;
      if (!anyAvailable) {
        action = `<span class="wishlist-card__soldout">Sold out</span>`;
      } else {
        const vid = variant && variant.available ? variant.id : '';
        const disabled = vid ? '' : ' disabled';
        action = `
          <button
            type="button"
            class="wishlist-card__add"
            data-wl-add
            data-handle="${p.handle}"
            data-variant-id="${vid}"
            ${disabled}
          >Add to cart</button>`;
      }

      return `
        <div class="wishlist-card wishlist-card--cart" data-wl-card data-handle="${p.handle}">
          <a href="${p.url}" class="wishlist-card__media-link" tabindex="-1" aria-hidden="true">${imgTag}</a>
          <div class="wishlist-card__body">
            <a href="${p.url}" class="wishlist-card__title">${p.title}</a>
            ${sizeBlock}
            <div class="wishlist-card__price" data-wl-price>${formatMoney(variant ? variant.price : p.price)}</div>
            <div class="wishlist-card__actions">
              ${action}
              <button type="button" class="wishlist-card__remove" data-wl-remove data-handle="${p.handle}">Remove</button>
            </div>
          </div>
        </div>`;
    }

    productFromCard(card) {
      const handle = card && card.getAttribute('data-handle');
      return (handle && this._products && this._products[handle]) || null;
    }

    onSelect(e) {
      const sel = e.target.closest('[data-wl-size]');
      if (!sel) return;
      const card = sel.closest('[data-wl-card]');
      if (!card) return;
      const product = this.productFromCard(card);
      if (!product) return;
      const v = product.variants.find((x) => String(x.id) === String(sel.value));
      const priceEl = card.querySelector('[data-wl-price]');
      if (priceEl && v) priceEl.textContent = formatMoney(v.price);
      const sizeText = card.querySelector('.wishlist-card__size-text');
      if (sizeText && v) sizeText.textContent = `Size: ${v.title || '—'}`;
      const addBtn = card.querySelector('[data-wl-add]');
      if (addBtn) {
        if (v && v.available) {
          addBtn.dataset.variantId = String(v.id);
          addBtn.disabled = false;
        } else {
          addBtn.dataset.variantId = '';
          addBtn.disabled = true;
        }
      }
      /* Persist chosen size in wishlist store (+ cart attribute sync) */
      const handle = card.getAttribute('data-handle');
      const arr = wlRead();
      const idx = arr.findIndex((i) => i.handle === handle);
      if (idx >= 0 && v) {
        arr[idx].variantId = v.id;
        wlWrite(arr);
      }
    }

    onClick(e) {
      const remove = e.target.closest('[data-wl-remove]');
      if (remove) {
        e.preventDefault();
        e.stopPropagation();
        const handle = remove.getAttribute('data-handle');
        /* Optimistic UI: drop card immediately (no list clear → no white flash) */
        this._skipNextRender = true;
        this.removeCard(handle);
        wlToggle(handle);
        return;
      }
      const add = e.target.closest('[data-wl-add]');
      if (add) {
        e.preventDefault();
        e.stopPropagation();
        this.addToCart(add);
      }
    }

    async addToCart(btn) {
      const card = btn.closest('[data-wl-card]');
      let variantId = btn.getAttribute('data-variant-id');
      const sizeSel = card && card.querySelector('[data-wl-size]');
      if (sizeSel && sizeSel.value) variantId = sizeSel.value;
      if (!variantId) return;

      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      try {
        const sections = prepareSections();
        const res = await fetch(`${root()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sections.join(','),
            sections_url: window.location.pathname
          })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Add failed');
        const cart = await fetchCartJson();
        cart.sections = data.sections || {};
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              onSuccessDo: 'force_open_drawer',
              cart
            }
          })
        );
        if (!data.sections || !Object.keys(data.sections).length) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
        /* Switch drawer to cart tab so shopper sees the item */
        const tabs = this.closest('cart-drawer') && this.closest('cart-drawer').querySelector('cart-wishlist-tabs');
        if (tabs && typeof tabs.select === 'function') tabs.select('cart');
      } catch (err) {
        console.warn('[wishlist] add to cart', err);
        btn.insertAdjacentHTML(
          'afterend',
          `<p class="wishlist-card__error text-xs" role="alert">${(err && err.message) || 'Could not add to cart'}</p>`
        );
        setTimeout(() => card && card.querySelector('.wishlist-card__error')?.remove(), 3500);
      } finally {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
      }
    }
  }
  if (!customElements.get('wishlist-panel')) customElements.define('wishlist-panel', WishlistPanel);

  /* ---------- <cart-line-size> : change size in cart drawer ------------- */
  class CartLineSize extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('[data-cart-size-select]');
      if (!this.select) return;
      this._prev = this.select.value;
      this._onChange = (e) => this.onChange(e);
      this.select.addEventListener('change', this._onChange);
    }
    disconnectedCallback() {
      if (this.select) this.select.removeEventListener('change', this._onChange);
    }

    async onChange() {
      const select = this.select;
      const newVariantId = select.value;
      const lineKey = this.getAttribute('data-line-key');
      const quantity = parseInt(this.getAttribute('data-quantity') || '1', 10) || 1;
      const oldVariantId = this.getAttribute('data-variant-id');
      if (!newVariantId || !lineKey || String(newVariantId) === String(oldVariantId)) {
        select.value = this._prev;
        return;
      }

      select.disabled = true;
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      try {
        const sections = prepareSections();
        /* 1) Remove current line */
        const rem = await fetch(`${root()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: lineKey,
            quantity: 0,
            sections: sections.join(',')
          })
        });
        if (!rem.ok) {
          const err = await rem.json().catch(() => ({}));
          throw new Error(err.description || 'Could not update cart');
        }
        /* 2) Add replacement variant with same qty */
        const add = await fetch(`${root()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(newVariantId), quantity }],
            sections: sections.join(','),
            sections_url: window.location.pathname
          })
        });
        const addData = await add.json();
        if (!add.ok) throw new Error(addData.description || addData.message || 'Size unavailable');

        const cart = await fetchCartJson();
        cart.sections = addData.sections || {};
        /* Prefer sections from add; if empty, force full drawer refresh */
        if (!cart.sections || !Object.keys(cart.sections).length) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          dispatchCartUpdate(cart);
        } else {
          dispatchCartUpdate(cart);
        }
        this._prev = newVariantId;
        this.setAttribute('data-variant-id', String(newVariantId));
      } catch (err) {
        console.warn('[cart-line-size]', err);
        select.value = this._prev;
        this.querySelector('[role="alert"]')?.remove();
        this.insertAdjacentHTML(
          'beforeend',
          `<p class="h-stack gap-2 text-xs" role="alert" style="color:#bf1515;margin-top:4px">${(err && err.message) || 'Could not change size'}</p>`
        );
        setTimeout(() => this.querySelector('[role="alert"]')?.remove(), 3500);
      } finally {
        select.disabled = false;
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }
  }
  if (!customElements.get('cart-line-size')) customElements.define('cart-line-size', CartLineSize);

  /* ---------- <cart-wishlist-tabs> : the drawer toggle pill ------------- */
  class CartWishlistTabs extends HTMLElement {
    connectedCallback() {
      this.cartBtn = this.querySelector('[data-tab="cart"]');
      this.wlBtn = this.querySelector('[data-tab="wishlist"]');
      this.cartBtn && this.cartBtn.addEventListener('click', () => this.select('cart'));
      this.wlBtn && this.wlBtn.addEventListener('click', () => this.select('wishlist'));
      this._openWl = () => this.select('wishlist');
      document.addEventListener('wishlist:open', this._openWl);
      this.select('cart');
    }
    disconnectedCallback() { document.removeEventListener('wishlist:open', this._openWl); }
    select(which) {
      const drawer = this.closest('cart-drawer') || document.getElementById('cart-drawer');
      if (!drawer) return;
      const isCart = which === 'cart';
      const cartPanel = drawer.querySelector('[data-panel="cart"]');
      const wlPanel = drawer.querySelector('[data-panel="wishlist"]');
      cartPanel && cartPanel.toggleAttribute('hidden', !isCart);
      wlPanel && wlPanel.toggleAttribute('hidden', isCart);
      this.cartBtn && this.cartBtn.setAttribute('aria-selected', isCart ? 'true' : 'false');
      this.wlBtn && this.wlBtn.setAttribute('aria-selected', isCart ? 'false' : 'true');
      // checkout footer is only meaningful for the cart
      const footer = drawer.querySelector('[slot="footer"]');
      if (footer) footer.style.display = isCart ? '' : 'none';
      if (!isCart && wlPanel) {
        const panel = wlPanel.querySelector('wishlist-panel');
        panel && panel.render();
      }
    }
  }
  if (!customElements.get('cart-wishlist-tabs')) customElements.define('cart-wishlist-tabs', CartWishlistTabs);

  /* ======================================================================
     DYNAMIC CART CROSS-SELL  ("Add a little extra")
     Fetches complementary products for the first cart item, related fallback.
     + arrows + dots so swipe is obvious
     ====================================================================== */
  class CartCrossSell extends HTMLElement {
    connectedCallback() {
      this.listEl = this.querySelector('[data-cross-sell-list]');
      this.wrap = this.closest('[data-cross-sell-wrap]');
      this.prevBtn = this.wrap && this.wrap.querySelector('[data-xtra-prev]');
      this.nextBtn = this.wrap && this.wrap.querySelector('[data-xtra-next]');
      this.dotsEl = this.wrap && this.wrap.querySelector('[data-xtra-dots]');
      this._onScroll = () => this.syncNav();
      this._onPrev = (e) => { e.preventDefault(); this.scrollByCard(-1); };
      this._onNext = (e) => { e.preventDefault(); this.scrollByCard(1); };
      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);
      this.load();
      this._onCart = (e) => {
        const items = e.detail && e.detail.cart && e.detail.cart.items;
        const pid = items && items[0] && items[0].product_id;
        if (pid && String(pid) !== this.getAttribute('data-product-id')) {
          this.setAttribute('data-product-id', String(pid));
          this.load();
        }
      };
      document.addEventListener('cart:change', this._onCart);
    }
    disconnectedCallback() {
      document.removeEventListener('cart:change', this._onCart);
      if (this.listEl) this.listEl.removeEventListener('scroll', this._onScroll);
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);
    }

    async fetchList(intent) {
      const pid = this.getAttribute('data-product-id');
      const limit = this.getAttribute('data-limit') || 6;
      const section = this.getAttribute('data-section-id') || 'cart-recommendations';
      const url = `${root()}recommendations/products?product_id=${pid}&intent=${intent}&limit=${limit}&section_id=${section}`;
      const res = await fetch(url);
      if (!res.ok) return null;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      return doc.querySelector('[data-cross-sell-list]');
    }

    async load() {
      if (!this.getAttribute('data-product-id') || !this.listEl) { this.hide(); return; }
      try {
        let list = await this.fetchList('complementary');
        if (!list || !list.querySelector('.horizontal-product-card')) {
          list = await this.fetchList('related');
        }
        if (list && list.querySelector('.horizontal-product-card')) {
          if (this.listEl) this.listEl.removeEventListener('scroll', this._onScroll);
          this.listEl.replaceWith(list);
          this.listEl = list;
          this.listEl.addEventListener('scroll', this._onScroll, { passive: true });
          this.buildDots();
          this.syncNav();
          this.show();
        } else {
          this.hide();
        }
      } catch (e) { this.hide(); }
    }

    cards() {
      if (!this.listEl) return [];
      return Array.from(this.listEl.querySelectorAll('.horizontal-product-card'));
    }

    buildDots() {
      if (!this.dotsEl) return;
      const cards = this.cards();
      this.dotsEl.innerHTML = '';
      if (cards.length <= 1) return;
      cards.forEach((card, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'suhi-xtra__dot' + (i === 0 ? ' is-active' : '');
        b.setAttribute('aria-label', `Go to product ${i + 1}`);
        b.addEventListener('click', (e) => {
          e.preventDefault();
          card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        });
        this.dotsEl.appendChild(b);
      });
    }

    activeIndex() {
      const cards = this.cards();
      if (!cards.length || !this.listEl) return 0;
      const left = this.listEl.scrollLeft;
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((c, i) => {
        const dist = Math.abs(c.offsetLeft - left);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      return best;
    }

    scrollByCard(dir) {
      const cards = this.cards();
      if (!cards.length) return;
      const i = Math.max(0, Math.min(cards.length - 1, this.activeIndex() + dir));
      cards[i].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }

    syncNav() {
      const cards = this.cards();
      const i = this.activeIndex();
      if (this.prevBtn) this.prevBtn.disabled = i <= 0;
      if (this.nextBtn) this.nextBtn.disabled = i >= cards.length - 1;
      if (this.dotsEl) {
        this.dotsEl.querySelectorAll('.suhi-xtra__dot').forEach((d, di) => {
          d.classList.toggle('is-active', di === i);
        });
      }
    }

    show() { this.wrap && this.wrap.removeAttribute('hidden'); }
    hide() { this.wrap && this.wrap.setAttribute('hidden', ''); }
  }
  if (!customElements.get('cart-cross-sell')) customElements.define('cart-cross-sell', CartCrossSell);

  /* ---------- header heart: open drawer in wishlist view ---------------- */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-wishlist-open]');
    if (trigger) document.dispatchEvent(new CustomEvent('wishlist:open'));
  });

  /* ---------- Collection size pills → PDP size carry-over (desktop) ----- */
  const SIZE_KEY = (handle) => `suhi:sizePick:${handle}`;

  document.addEventListener('click', (e) => {
    const pill = e.target.closest('a.product-card__size[data-suhi-variant]');
    if (!pill || pill.getAttribute('aria-disabled') === 'true') return;
    const handle = pill.getAttribute('data-suhi-handle');
    const variantId = pill.getAttribute('data-suhi-variant');
    const size = pill.getAttribute('data-suhi-size');
    if (!handle || !variantId) return;
    try {
      sessionStorage.setItem(SIZE_KEY(handle), JSON.stringify({
        variantId: String(variantId),
        size: size || '',
        at: Date.now()
      }));
    } catch (err) {}

    /* Highlight selected size on the card (if SPA keeps the page) */
    const wrap = pill.closest('[data-product-sizes]');
    if (wrap) {
      wrap.querySelectorAll('.product-card__size').forEach((a) => a.classList.remove('is-selected'));
      pill.classList.add('is-selected');
    }

    /* Keep product title / image links on the same variant for this card */
    const card = pill.closest('product-card');
    if (card) {
      const href = pill.getAttribute('href');
      card.querySelectorAll('a.product-card__media, a.product-title').forEach((a) => {
        if (href) a.setAttribute('href', href);
      });
    }
  }, true);
})();

/* ==========================================================================
   Suhi LOHIT runtime fixes + SPEED helpers
   ========================================================================== */
(() => {
  /* Collection → PDP: ensure selected size/variant is applied on product page */
  const applySizeCarryOver = () => {
    if (!document.body.classList.contains('template-product')) return;
    /* Guard against redirect loops */
    if (sessionStorage.getItem('suhi:sizeRedirecting') === '1') {
      sessionStorage.removeItem('suhi:sizeRedirecting');
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const urlVariant = params.get('variant');

    let handle = document.querySelector('variant-picker[handle]')?.getAttribute('handle') || '';
    if (!handle) {
      const path = window.location.pathname.replace(/\/$/, '');
      const m = path.match(/\/products\/([^/?#]+)/);
      if (m) handle = decodeURIComponent(m[1]);
    }
    if (!handle) return;

    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(`suhi:sizePick:${handle}`) || 'null');
    } catch (e) { saved = null; }

    if (urlVariant) {
      try {
        sessionStorage.setItem(`suhi:sizePick:${handle}`, JSON.stringify({
          variantId: String(urlVariant),
          size: (saved && saved.size) || '',
          at: Date.now()
        }));
      } catch (e) {}
      return;
    }

    if (saved && saved.variantId) {
      /* Stale picks (> 30 min) ignore */
      if (saved.at && (Date.now() - saved.at) > 30 * 60 * 1000) return;
      const u = new URL(window.location.href);
      u.searchParams.set('variant', String(saved.variantId));
      try { sessionStorage.setItem('suhi:sizeRedirecting', '1'); } catch (e) {}
      window.location.replace(u.toString());
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applySizeCarryOver);
  } else {
    applySizeCarryOver();
  }

  /* MOBILE STABILITY: loading bar, hard-nav only in iframes, passive scroll, video pause */
  const forceLoadingEnd = () => {
    try {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    } catch (e) {}
    document.querySelectorAll('loading-bar').forEach((el) => {
      el.removeAttribute('aria-busy');
      el.setAttribute('aria-hidden', 'true');
      el.style.opacity = '0';
      el.style.pointerEvents = 'none';
    });
  };
  /* If a cart op leaves the bar stuck, clear after 6s */
  let loadingWatch = null;
  document.addEventListener('theme:loading:start', () => {
    clearTimeout(loadingWatch);
    loadingWatch = setTimeout(forceLoadingEnd, 6000);
  });
  document.addEventListener('theme:loading:end', () => clearTimeout(loadingWatch));
  window.addEventListener('pageshow', forceLoadingEnd);

  /* iOS: blur inputs on scroll so keyboard doesn't leave layout glitched */
  let scrollBlurTimer = null;
  window.addEventListener('scroll', () => {
    if (!scrollBlurTimer) {
      scrollBlurTimer = setTimeout(() => {
        scrollBlurTimer = null;
        const ae = document.activeElement;
        if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.tagName === 'SELECT')) {
          if (window.matchMedia('(max-width: 999px)').matches && ae.closest('.suhi-stsearch')) {
            /* keep sticky search focused while typing */
          } else if (window.matchMedia('(max-width: 699px)').matches && document.body.scrollTop > 0) {
            /* don't auto-blur product form on intentional typing */
          }
        }
      }, 200);
    }
  }, { passive: true });

  /* 3) Sticky search scroll logic lives in sticky-search.liquid (idle retract + scroll-up only).
     Only enforce hide on collection/product templates here. */
  document.querySelectorAll('.suhi-stsearch').forEach((root) => {
    if (root.dataset.suhiTplFix) return;
    root.dataset.suhiTplFix = '1';
    const hideOnTpl = () => {
      const b = document.body;
      if (!b) return;
      if (b.classList.contains('template-collection') || b.classList.contains('template-product')) {
        root.style.display = 'none';
      }
    };
    hideOnTpl();
    document.addEventListener('DOMContentLoaded', hideOnTpl);
  });

  /* SPEED: ensure offscreen product images are lazy + async decode */
  const tuneImages = () => {
    const isPdp = document.body && document.body.classList.contains('template-product');
    const isMobile = window.matchMedia('(max-width: 699px)').matches;
    /* Desktop grid_2x shows many images — only first 2 eager; rest lazy */
    const eagerCount = isPdp ? (isMobile ? 1 : 2) : 2;
    document.querySelectorAll('#main img').forEach((img, i) => {
      if (!img.getAttribute('decoding')) img.decoding = 'async';
      if (isPdp) {
        if (i < eagerCount) {
          if (i === 0) {
            img.loading = 'eager';
            try { img.fetchPriority = 'high'; } catch (_) {}
          }
          return;
        }
        img.loading = 'lazy';
        try { img.fetchPriority = 'low'; } catch (_) {}
        return;
      }
      if (i < 2) return;
      if (!img.getAttribute('loading')) img.loading = 'lazy';
    });
    /* Below-fold product videos: never autoplay offscreen hard */
    if (isPdp) {
      document.querySelectorAll('#main video, .suhi-pv video, [id^="suhi-reels-"] video').forEach((v, i) => {
        if (i === 0 && isMobile) return;
        v.setAttribute('preload', 'none');
        if (!v.hasAttribute('playsinline')) v.setAttribute('playsinline', '');
      });
    }
    document.querySelectorAll('#main iframe:not([loading])').forEach((f, i) => {
      if (i === 0) return;
      f.loading = 'lazy';
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', tuneImages);
  else tuneImages();
  /* Re-tune after theme section swaps */
  document.addEventListener('shopify:section:load', tuneImages);

  /*
    Hard navigation for collection image banners — ONLY inside theme preview iframes.
    On real mobile/desktop storefront, normal links (faster, less glitchy).
  */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a.suhi-iwtb-image-link, a[data-suhi-hard-nav]');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || href === '#' || href.startsWith('javascript:')) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    let inIframe = false;
    try { inIframe = window.top !== window.self; } catch (_) { inIframe = true; }
    if (!inIframe) return; /* normal navigation on live site */
    e.preventDefault();
    e.stopPropagation();
    try { window.top.location.assign(href); }
    catch (_) { window.location.assign(href); }
  }, true);

  /* Pause all reels when tab hidden (mobile battery + audio glitches) */
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) return;
    document.querySelectorAll('.suhi-reels__media video').forEach((v) => {
      try { v.pause(); } catch (_) {}
      v.muted = true;
    });
  });

  /* USP rotator under Add to Cart — auto-advance only while in view (no layout thrash) */
  const initUspRotators = () => {
    document.querySelectorAll('[data-suhi-usp]').forEach((root) => {
      if (root.dataset.uspInit) return;
      root.dataset.uspInit = '1';
      const slides = Array.from(root.querySelectorAll('[data-suhi-usp-slide]'));
      const dots = Array.from(root.querySelectorAll('[data-suhi-usp-dot]'));
      if (slides.length < 2) return;
      let i = 0;
      let inView = true;
      const isMobile = window.matchMedia('(max-width: 699px)').matches;
      const baseMs = Math.max(2000, parseInt(root.getAttribute('data-interval') || '3500', 10));
      /* Slightly slower on mobile to cut main-thread work */
      const ms = isMobile ? Math.max(baseMs, 4200) : baseMs;
      const show = (n) => {
        i = ((n % slides.length) + slides.length) % slides.length;
        slides.forEach((s, si) => {
          const on = si === i;
          s.classList.toggle('is-active', on);
          s.setAttribute('aria-hidden', on ? 'false' : 'true');
        });
        dots.forEach((d, di) => d.classList.toggle('is-active', di === i));
      };
      dots.forEach((d) => {
        d.addEventListener('click', () => {
          show(parseInt(d.getAttribute('data-suhi-usp-dot') || '0', 10));
        });
      });
      let timer = null;
      const start = () => {
        if (timer) return;
        timer = setInterval(() => {
          if (document.hidden || !inView) return;
          show(i + 1);
        }, ms);
      };
      const stop = () => {
        if (!timer) return;
        clearInterval(timer);
        timer = null;
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            inView = !!(entries[0] && entries[0].isIntersecting);
            if (inView) start();
            else stop();
          },
          { root: null, threshold: 0.15 }
        );
        io.observe(root);
      } else {
        start();
      }
      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', () => { if (inView) start(); });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initUspRotators);
  else initUspRotators();

  /* ======================================================================
     Suhi Squad: stop hotspot open from scaling/jumping the look image.
     Theme runs changeLookFocalPoint → transform scale on the carousel;
     with full-bleed layout that becomes a visible glitch (user video).
     ====================================================================== */
  (function suhiSquadNoZoomGlitch() {
    /* No-op on PDP / pages without squad — avoids document-wide observers */
    const hasSquad = () =>
      !!document.querySelector(
        'shop-the-look-mobile-carousel, .shop-the-look__carousel, [data-suhi-squad-loop], .shopify-section--Suhi_squad, .shopify-section--shop-the-look'
      );
    const neutralize = (el) => {
      if (!el || !el.classList || !el.classList.contains('is-expanded')) return;
      el.style.setProperty('transform', 'none', 'important');
    };
    const scan = () => {
      document
        .querySelectorAll(
          'shop-the-look-mobile-carousel.is-expanded, .shop-the-look__carousel.is-expanded'
        )
        .forEach(neutralize);
    };
    const obs = new MutationObserver((muts) => {
      muts.forEach((m) => {
        if (m.type === 'attributes' && m.attributeName === 'class') {
          neutralize(m.target);
        }
        if (m.type === 'attributes' && m.attributeName === 'style') {
          const t = m.target;
          if (
            t &&
            t.tagName === 'SHOP-THE-LOOK-MOBILE-CAROUSEL' &&
            t.classList.contains('is-expanded')
          ) {
            if (t.style.transform && t.style.transform !== 'none') {
              t.style.setProperty('transform', 'none', 'important');
            }
          }
        }
      });
    });
    const boot = () => {
      if (!hasSquad()) return;
      document
        .querySelectorAll('shop-the-look-mobile-carousel, .shop-the-look__carousel')
        .forEach((el) => {
          if (el.dataset.suhiSquadObs) return;
          el.dataset.suhiSquadObs = '1';
          obs.observe(el, { attributes: true, attributeFilter: ['class', 'style'] });
        });
      scan();
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
    document.addEventListener('shopify:section:load', () => setTimeout(boot, 50));
    /* Header hide only when squad exists */
    if (hasSquad() || document.readyState === 'loading') {
      const headerObs = new MutationObserver(() => {
        if (!hasSquad()) return;
        if (document.documentElement.style.getPropertyValue('--hide-header-group') === '1') {
          if (document.querySelector('shop-the-look-mobile-carousel.is-expanded')) {
            document.documentElement.style.removeProperty('--hide-header-group');
          }
        }
      });
      const startHeaderObs = () => {
        if (!hasSquad()) return;
        headerObs.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['style'],
        });
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', startHeaderObs);
      } else {
        startHeaderObs();
      }
    }
  })();

  /* ======================================================================
     PDP gallery: mobile-style dots + single-image carousel on all widths
     ====================================================================== */
  (function pdpGalleryDotsFix() {
    const CSS =
      /* Small page dots (mobile + desktop carousel) */
      'body.template-product product-gallery .page-dots,' +
      'body.template-product product-gallery carousel-navigation.page-dots{' +
      'display:flex!important;visibility:visible!important;height:auto!important;' +
      'margin:12px 0 0!important;padding:0!important;overflow:visible!important;' +
      'pointer-events:auto!important;justify-content:center;gap:8px;}' +
      'body.template-product product-gallery .page-dots>button,' +
      'body.template-product product-gallery .page-dots>button.tap-area{' +
      'all:unset!important;display:inline-block!important;width:7px!important;height:7px!important;' +
      'border-radius:50%!important;background:#c8c8c8!important;cursor:pointer!important;overflow:hidden!important;}' +
      'body.template-product product-gallery .page-dots>button:before,' +
      'body.template-product product-gallery .page-dots>button:after{content:none!important;display:none!important;}' +
      'body.template-product product-gallery .page-dots>button[aria-current="true"]{background:#1c1c1c!important;}' +
                  /* No beige plate */
      'body.template-product .product-gallery,body.template-product .product-gallery__media,body.template-product .product-media,body.template-product .product-gallery__carousel,body.template-product .product-gallery__image-list{background:transparent!important;background-color:transparent!important;}' +
      /* Mobile: natural image height (carousel) */
      '@media (max-width:999px){' +
      'body.template-product .product-gallery__media{aspect-ratio:auto!important;height:auto!important;overflow:hidden!important;min-width:100%!important;}' +
      'body.template-product .product-gallery img{height:auto!important;width:100%!important;object-fit:cover!important;}' +
      '}' +
      /* Desktop Diris layout-3: 2-col image grid + sticky info */
      '@media (min-width:1000px){' +
      'body.template-product .product-gallery{position:relative!important;top:auto!important;width:100%!important;max-width:100%!important;margin:0!important;overflow:visible!important;background:transparent!important;max-height:none!important;}' +
      'body.template-product .product-gallery__image-list{width:100%!important;max-width:100%!important;margin:0!important;}' +
      'body.template-product .product-gallery__carousel{display:grid!important;grid-template-columns:repeat(2,minmax(0,1fr))!important;grid-auto-flow:dense!important;gap:10px!important;scroll-snap-type:none!important;overflow:visible!important;width:100%!important;}' +
      'body.template-product .product-gallery__media,body.template-product .product-media{width:100%!important;min-width:0!important;max-width:100%!important;flex:none!important;aspect-ratio:2/3!important;max-height:none!important;overflow:hidden!important;background:transparent!important;scroll-snap-align:unset!important;grid-column:auto!important;}' +
      'body.template-product .product-gallery img{width:100%!important;height:100%!important;max-height:none!important;object-fit:cover!important;cursor:zoom-in!important;}' +
      'body.template-product .product-info{position:sticky!important;top:calc(var(--sticky-area-height,72px) + 24px)!important;align-self:start!important;max-height:calc(100vh - var(--sticky-area-height,72px) - 40px);overflow-y:auto!important;}' +
      'body.template-product product-gallery .page-dots{display:none!important;}' +
      'body.template-product .product-gallery__carousel-with-arrows>carousel-prev-button,body.template-product .product-gallery__carousel-with-arrows>carousel-next-button{display:none!important;}' +
      '}';


    const inject = () => {
      let s = document.getElementById('suhi-pdp-dots-fix');
      if (!s) {
        s = document.createElement('style');
        s.id = 'suhi-pdp-dots-fix';
        document.documentElement.appendChild(s);
      }
      s.textContent = CSS;
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', inject);
    } else {
      inject();
    }
  })();

  /* ======================================================================
     PDP gallery: click-to-zoom + soft wrap loop (mobile-perf conscious)
     ====================================================================== */
  (function pdpGalleryEnhance() {
    const isPdp = () =>
      document.body && document.body.classList.contains('template-product');

    /* Skip all gallery work on non-product pages */
    if (document.readyState !== 'loading' && document.body && !isPdp()) {
      /* still allow late product:rerender / section load */
    }

    /* Logical gallery index (ignores triple-buffer clones) */
    const mediaLogicalIndex = (media, track) => {
      if (!media) return -1;
      const g = media.getAttribute('data-suhi-gidx');
      if (g != null && g !== '') {
        const n = parseInt(g, 10);
        if (!isNaN(n)) return n;
      }
      const mids = Array.from(
        (track || media.parentElement || document).querySelectorAll(
          '.product-gallery__media[data-suhi-set="mid"]:not([hidden]), .product-gallery__media:not([data-suhi-clone]):not([hidden])'
        )
      ).filter((el) => !el.hasAttribute('data-suhi-clone') || el.getAttribute('data-suhi-set') === 'mid');
      /* Prefer mid set only */
      const midOnly = Array.from(
        (track || media.parentElement).querySelectorAll('.product-gallery__media[data-suhi-set="mid"]:not([hidden])')
      );
      if (midOnly.length) {
        const same = midOnly.find((m) => m.getAttribute('data-suhi-gidx') === media.getAttribute('data-suhi-gidx'));
        if (same) return parseInt(same.getAttribute('data-suhi-gidx') || '0', 10) || 0;
      }
      return mids.indexOf(media);
    };

    const syncLightboxChrome = () => {
      const open = !!document.querySelector('.pswp--open');
      document.documentElement.classList.toggle('suhi-pswp-open', open);
      document.body.classList.toggle('suhi-pswp-open', open);
    };

    const watchPswp = () => {
      if (watchPswp._done) return;
      watchPswp._done = true;
      /* Cheap poll only while a pswp node exists — avoid document-wide MutationObserver */
      let poll = 0;
      const tick = () => {
        syncLightboxChrome();
        if (document.querySelector('.pswp')) {
          poll = window.setTimeout(tick, 400);
        } else {
          poll = 0;
        }
      };
      document.addEventListener(
        'click',
        (e) => {
          if (!isPdp()) return;
          /* Start light poll after lightbox open intent */
          if (e.target.closest?.('.product-gallery__media img, .product-gallery__zoom-button')) {
            if (!poll) poll = window.setTimeout(tick, 80);
          }
          const pswp = e.target.closest?.('.pswp--open');
          if (!pswp) return;
          if (
            e.target.classList.contains('pswp__item') ||
            e.target.classList.contains('pswp__bg') ||
            e.target.classList.contains('pswp__img--placeholder')
          ) {
            const closeBtn = pswp.querySelector(
              'button.pswp__button--close-button, .pswp__top-bar .circle-button'
            );
            if (closeBtn) closeBtn.click();
          }
        },
        true
      );
      document.addEventListener(
        'keydown',
        (e) => {
          if (e.key !== 'Escape') return;
          const pswpRoot = document.querySelector('.pswp--open');
          if (!pswpRoot) return;
          const closeBtn = pswpRoot.querySelector(
            'button.pswp__button--close-button, .pswp__top-bar .circle-button, button[aria-label*="lose" i]'
          );
          if (closeBtn) closeBtn.click();
          else {
            pswpRoot.classList.remove('pswp--open');
            document.documentElement.classList.remove('suhi-pswp-open');
            document.body.classList.remove('suhi-pswp-open');
          }
        },
        true
      );
    };

    /*
      Click image → open zoom lightbox (desktop + mobile).
      Theme carousel was eating the click as "next slide"; we capture first,
      stop all propagation, then open PhotoSwipe via gallery API / event.
    */
    let dragMoved = false;
    let dragX = 0;
    let dragY = 0;
    let tracking = false;
    document.addEventListener(
      'pointerdown',
      (e) => {
        if (!isPdp()) return;
        if (!e.target.closest?.('.product-gallery__carousel')) return;
        if (document.body.classList.contains('suhi-pswp-open')) return;
        /* Ignore arrow buttons */
        if (e.target.closest?.('carousel-prev-button, carousel-next-button, button.tap-area')) {
          tracking = false;
          return;
        }
        dragMoved = false;
        dragX = e.clientX;
        dragY = e.clientY;
        tracking = true;
      },
      { passive: true }
    );
    document.addEventListener(
      'pointermove',
      (e) => {
        if (!tracking) return;
        /* Only treat as swipe if clearly dragged (avoid blocking zoom on tiny jitter) */
        if (Math.abs(e.clientX - dragX) > 18 || Math.abs(e.clientY - dragY) > 18) {
          dragMoved = true;
        }
      },
      { passive: true }
    );
    const endTrack = () => { tracking = false; };
    document.addEventListener('pointerup', endTrack, { passive: true });
    document.addEventListener('pointercancel', endTrack, { passive: true });

    document.addEventListener(
      'click',
      (e) => {
        if (!isPdp()) return;
        if (document.body.classList.contains('suhi-pswp-open')) return;
        if (e.target.closest?.('carousel-prev-button, carousel-next-button, .page-dots, button.tap-area')) return;
        const img = e.target.closest?.('.product-gallery__media[data-media-type="image"] img, .product-gallery__media[data-media-type="image"]');
        if (!img) return;
        const media = img.closest?.('.product-gallery__media') || (img.classList?.contains('product-gallery__media') ? img : null);
        if (!media || media.getAttribute('data-media-type') !== 'image') return;
        if (media.closest('quick-buy-modal')) return;
        if (dragMoved) return; /* was a swipe between images */

        const gallery = media.closest('product-gallery');
        if (!gallery) return;
        const carousel = gallery.carousel || gallery.querySelector('.product-gallery__carousel');
        if (!carousel) return;

        let idx = mediaLogicalIndex(media, carousel);
        if (idx < 0) {
          const imageCells = Array.from(
            carousel.querySelectorAll(
              '.product-gallery__media[data-media-type="image"][data-suhi-set="mid"]:not([hidden]), .product-gallery__media[data-media-type="image"]:not([data-suhi-clone]):not([hidden])'
            )
          );
          idx = imageCells.indexOf(media);
        }
        if (idx < 0) return;

        /* Kill carousel click advance + theme default */
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();

        try {
          /* Prefer theme event (same path as native desktop zoom) */
          gallery.dispatchEvent(
            new CustomEvent('lightbox:open', { bubbles: true, detail: { index: idx } })
          );
          if (typeof gallery.openLightBox === 'function') {
            gallery.openLightBox(idx);
          }
          setTimeout(syncLightboxChrome, 60);
          setTimeout(syncLightboxChrome, 280);
        } catch (_) {}
      },
      true
    );

    /*
      Mobile gallery infinite loop — same idea as fullscreen reels:
      [before clones][mid originals][after clones]
      Normalize ONLY after scroll settles so last image always plays, then
      one more swipe lands on first seamlessly (and reverse).
      Desktop stays grid — no loop needed.
    */
    const wireLoop = (track) => {
      if (!track || track.dataset.suhiGalleryLoop) return;
      if (window.matchMedia('(min-width: 1000px)').matches) return;

      if (track.__suhiGalMo) {
        try {
          track.__suhiGalMo.disconnect();
        } catch (_) {}
        track.__suhiGalMo = null;
      }

      /* Tear any previous clone buffers before rebuild */
      track.querySelectorAll('.product-gallery__media[data-suhi-clone]').forEach((n) => n.remove());
      track.querySelectorAll('.product-gallery__media[data-suhi-set]').forEach((n) => {
        n.removeAttribute('data-suhi-set');
        n.removeAttribute('data-suhi-gidx');
      });

      const originals = Array.from(
        track.querySelectorAll(':scope > .product-gallery__media:not([hidden])')
      ).filter((el) => !el.hasAttribute('data-suhi-clone'));
      if (originals.length < 2) return;

      track.dataset.suhiGalleryLoop = '1';
      let normalizing = false;
      let building = true;

      originals.forEach((cell, i) => {
        cell.setAttribute('data-suhi-set', 'mid');
        cell.setAttribute('data-suhi-gidx', String(i));
      });

      const makeClone = (cell, i, setName) => {
        const cl = cell.cloneNode(true);
        cl.setAttribute('data-suhi-clone', '1');
        cl.setAttribute('data-suhi-set', setName);
        cl.setAttribute('data-suhi-gidx', String(i));
        cl.classList.remove('is-initial');
        cl.removeAttribute('id');
        /* Avoid duplicate form controls / video ids fighting originals */
        cl.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        cl.querySelectorAll('video').forEach((v) => {
          try {
            v.pause();
            v.removeAttribute('autoplay');
          } catch (_) {}
        });
        return cl;
      };

      /* before set */
      const first = originals[0];
      originals.forEach((cell, i) => {
        track.insertBefore(makeClone(cell, i, 'before'), first);
      });
      /* after set */
      originals.forEach((cell, i) => {
        track.appendChild(makeClone(cell, i, 'after'));
      });

      const midCell = (gidx) =>
        track.querySelector(
          `.product-gallery__media[data-suhi-set="mid"][data-suhi-gidx="${gidx}"]`
        );

      const goToMid = (gidx, updateSelect) => {
        const target = midCell(gidx);
        if (!target) return;
        normalizing = true;
        track.style.scrollSnapType = 'none';
        const left = target.offsetLeft;
        track.scrollLeft = left;
        if (updateSelect !== false && typeof track.select === 'function') {
          try {
            /* Cell index among all non-hidden children */
            const cells = Array.from(track.querySelectorAll(':scope > .product-gallery__media:not([hidden])'));
            const idx = cells.indexOf(target);
            if (idx >= 0) track.select(idx, { instant: true });
          } catch (_) {}
        }
        requestAnimationFrame(() => {
          track.style.scrollSnapType = '';
          /* Re-assert position after snap CSS returns */
          track.scrollLeft = target.offsetLeft;
          normalizing = false;
        });
      };

      /* Open on first real slide (mid set) after layout */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          goToMid(0, true);
          building = false;
        });
      });

      const nearest = () => {
        const slides = Array.from(
          track.querySelectorAll(':scope > .product-gallery__media:not([hidden])')
        );
        if (!slides.length) return null;
        const mid = track.scrollLeft + track.clientWidth / 2;
        let best = null;
        let bestDist = Infinity;
        slides.forEach((s) => {
          const center = s.offsetLeft + s.offsetWidth / 2;
          const dist = Math.abs(center - mid);
          if (dist < bestDist) {
            bestDist = dist;
            best = s;
          }
        });
        return best;
      };

      const onSettle = () => {
        if (normalizing) return;
        const slide = nearest();
        if (!slide) return;
        const set = slide.getAttribute('data-suhi-set') || 'mid';
        const gidx = parseInt(slide.getAttribute('data-suhi-gidx') || '0', 10) || 0;
        if (set === 'before' || set === 'after') {
          /* Past ends → silent snap to matching mid (same image, no flash) */
          goToMid(gidx, true);
        } else if (typeof track.select === 'function') {
          /* Keep theme selectedIndex in sync while on mid */
          try {
            const cells = Array.from(
              track.querySelectorAll(':scope > .product-gallery__media:not([hidden])')
            );
            const idx = cells.indexOf(slide);
            if (idx >= 0 && track.selectedIndex !== idx) {
              track.select(idx, { instant: true });
            }
          } catch (_) {}
        }
      };

      let settleT = 0;
      track.addEventListener(
        'scroll',
        () => {
          if (normalizing) return;
          clearTimeout(settleT);
          settleT = setTimeout(onSettle, 120);
        },
        { passive: true }
      );
      track.addEventListener('scrollend', onSettle);

      /* Rebuild if variant filter hides/shows media */
      track.__suhiGalMo = new MutationObserver(() => {
        if (normalizing || building) return;
        clearTimeout(track.__suhiGalRebuildT);
        track.__suhiGalRebuildT = setTimeout(() => {
          if (track.__suhiGalMo) {
            try {
              track.__suhiGalMo.disconnect();
            } catch (_) {}
            track.__suhiGalMo = null;
          }
          track.dataset.suhiGalleryLoop = '';
          wireLoop(track);
        }, 250);
      });
      track.__suhiGalMo.observe(track, {
        subtree: false,
        childList: false,
        attributes: true,
        attributeFilter: ['hidden']
      });
      /* Also watch direct children for hidden toggles */
      originals.forEach((el) => {
        track.__suhiGalMo.observe(el, { attributes: true, attributeFilter: ['hidden'] });
      });
    };

    /* Prestige openLightBox only finds :scope > img; images are often nested — patch it */
    const patchOpenLightBox = () => {
      document.querySelectorAll('product-gallery').forEach((g) => {
        if (g.__suhiLbPatch || typeof g.openLightBox !== 'function') return;
        g.__suhiLbPatch = true;
        const orig = g.openLightBox.bind(g);
        g.openLightBox = function (index) {
          try {
            const carousel = this.carousel || this.querySelector('.product-gallery__carousel');
            if (!carousel) return orig(index);
            const cells = Array.from(
              carousel.querySelectorAll('.product-gallery__media[data-media-type="image"]:not([hidden])')
            );
            const images = cells
              .map((cell) => cell.querySelector('img'))
              .filter(Boolean);
            if (!images.length) return orig(index);
            /* If native path would get 0 images (nested img), open via lightBox API directly */
            const direct = cells.flatMap((c) => Array.from(c.querySelectorAll(':scope > img')));
            if (direct.length >= images.length) return orig(index);
            const dataSource = images.map((image) => ({
              thumbnailElement: image,
              src: image.currentSrc || image.src,
              srcset: image.srcset || '',
              msrc: image.currentSrc || image.src,
              width: parseInt(image.getAttribute('width') || image.naturalWidth || 1200, 10),
              height: parseInt(image.getAttribute('height') || image.naturalHeight || 1600, 10),
              alt: image.alt || '',
              thumbCropped: true,
            }));
            const lb = this.lightBox;
            if (lb && typeof lb.loadAndOpen === 'function') {
              lb.loadAndOpen(Math.max(0, index || 0), dataSource);
              return;
            }
          } catch (_) {}
          return orig(index);
        };
      });
    };

    const initGalleries = () => {
      if (!isPdp()) return;
      patchOpenLightBox();
      document
        .querySelectorAll('body.template-product .product-gallery__carousel, product-gallery .product-gallery__carousel')
        .forEach((track) => {
          if (track.closest('quick-buy-modal')) return;
          /* Allow re-wire after variant filter rebuild */
          if (track.dataset.suhiGalleryLoop === '1' && track.querySelector('[data-suhi-set="mid"]')) return;
          track.dataset.suhiGalleryLoop = '';
          wireLoop(track);
        });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        if (!isPdp()) return;
        initGalleries();
        watchPswp();
      });
    } else if (isPdp()) {
      initGalleries();
      watchPswp();
    }
    window.addEventListener('load', () => { if (isPdp()) initGalleries(); }, { once: true });
    document.addEventListener('shopify:section:load', () => { if (isPdp()) initGalleries(); });
  })();

  /* ======================================================================
     Quick view / quick-add gallery: infinite loop (reels-style triple buffer)
     [before clones][mid originals][after clones] — settle-only normalize
     so last image plays, then next swipe continues to first (and reverse).
     ====================================================================== */
  (function quickBuyGalleryInfiniteLoop() {
    const mediaSel = ':scope > .product-gallery__media:not([hidden])';

    const wireQvLoop = (track) => {
      if (!track) return;
      /* Already wired with mid set */
      if (track.dataset.suhiQvLoop === '1' && track.querySelector('[data-suhi-qv-set="mid"]')) return;

      /* Tear previous buffers */
      track.querySelectorAll('.product-gallery__media[data-suhi-qv-clone]').forEach((n) => n.remove());
      track.querySelectorAll('.product-gallery__media[data-suhi-qv-set]').forEach((n) => {
        n.removeAttribute('data-suhi-qv-set');
        n.removeAttribute('data-suhi-qv-gidx');
      });

      const originals = Array.from(track.querySelectorAll(mediaSel)).filter(
        (el) => !el.hasAttribute('data-suhi-qv-clone')
      );
      if (originals.length < 2) {
        track.dataset.suhiQvLoop = '0';
        return;
      }

      track.dataset.suhiQvLoop = '1';
      let normalizing = false;
      let logicalIndex = 0;
      const count = originals.length;

      originals.forEach((cell, i) => {
        cell.setAttribute('data-suhi-qv-set', 'mid');
        cell.setAttribute('data-suhi-qv-gidx', String(i));
      });

      const makeClone = (cell, i, setName) => {
        const cl = cell.cloneNode(true);
        cl.setAttribute('data-suhi-qv-clone', '1');
        cl.setAttribute('data-suhi-qv-set', setName);
        cl.setAttribute('data-suhi-qv-gidx', String(i));
        cl.classList.remove('is-initial');
        cl.removeAttribute('id');
        cl.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        cl.querySelectorAll('video').forEach((v) => {
          try {
            v.pause();
            v.removeAttribute('autoplay');
          } catch (_) {}
        });
        return cl;
      };

      const first = originals[0];
      originals.forEach((cell, i) => {
        track.insertBefore(makeClone(cell, i, 'before'), first);
      });
      originals.forEach((cell, i) => {
        track.appendChild(makeClone(cell, i, 'after'));
      });

      const midCell = (gidx) =>
        track.querySelector(
          `.product-gallery__media[data-suhi-qv-set="mid"][data-suhi-qv-gidx="${gidx}"]`
        );

      const goToMid = (gidx, smooth) => {
        gidx = ((gidx % count) + count) % count;
        const target = midCell(gidx);
        if (!target) return;
        logicalIndex = gidx;
        normalizing = true;
        track.style.scrollSnapType = 'none';
        const left = target.offsetLeft;
        if (smooth) {
          track.scrollTo({ left, behavior: 'smooth' });
          setTimeout(() => {
            track.style.scrollSnapType = '';
            track.scrollLeft = target.offsetLeft;
            normalizing = false;
          }, 380);
        } else {
          track.scrollLeft = left;
          if (typeof track.select === 'function') {
            try {
              const cells = Array.from(track.querySelectorAll(mediaSel));
              const idx = cells.indexOf(target);
              if (idx >= 0) track.select(idx, { instant: true });
            } catch (_) {}
          }
          requestAnimationFrame(() => {
            track.style.scrollSnapType = '';
            track.scrollLeft = target.offsetLeft;
            normalizing = false;
          });
        }
      };

      /* Land on first real image after layout */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => goToMid(0, false));
      });

      const nearest = () => {
        const slides = Array.from(track.querySelectorAll(mediaSel));
        if (!slides.length) return null;
        const mid = track.scrollLeft + track.clientWidth / 2;
        let best = null;
        let bestDist = Infinity;
        slides.forEach((s) => {
          const center = s.offsetLeft + s.offsetWidth / 2;
          const dist = Math.abs(center - mid);
          if (dist < bestDist) {
            bestDist = dist;
            best = s;
          }
        });
        return best;
      };

      const onSettle = () => {
        if (normalizing) return;
        const slide = nearest();
        if (!slide) return;
        const set = slide.getAttribute('data-suhi-qv-set') || 'mid';
        const gidx = parseInt(slide.getAttribute('data-suhi-qv-gidx') || '0', 10) || 0;
        if (set === 'before' || set === 'after') {
          goToMid(gidx, false);
        } else {
          logicalIndex = gidx;
          if (typeof track.select === 'function') {
            try {
              const cells = Array.from(track.querySelectorAll(mediaSel));
              const idx = cells.indexOf(slide);
              if (idx >= 0 && track.selectedIndex !== idx) {
                track.select(idx, { instant: true });
              }
            } catch (_) {}
          }
        }
      };

      let settleT = 0;
      track.addEventListener(
        'scroll',
        () => {
          if (normalizing) return;
          clearTimeout(settleT);
          settleT = setTimeout(onSettle, 120);
        },
        { passive: true }
      );
      track.addEventListener('scrollend', onSettle);

      /* Arrows: step mid set with wrap (never stuck on last) */
      const root =
        track.closest('.quick-buy-modal__gallery-wrapper') ||
        track.closest('quick-buy-modal') ||
        track.closest('product-gallery');
      if (root && !root.dataset.suhiQvArrows) {
        root.dataset.suhiQvArrows = '1';

        const goNext = (e) => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
            if (e.stopImmediatePropagation) e.stopImmediatePropagation();
          }
          /* If on last mid, scroll into after-clone then settle → first */
          if (logicalIndex === count - 1) {
            const after0 = track.querySelector(
              '.product-gallery__media[data-suhi-qv-set="after"][data-suhi-qv-gidx="0"]'
            );
            if (after0) {
              normalizing = false;
              track.scrollTo({ left: after0.offsetLeft, behavior: 'smooth' });
              setTimeout(onSettle, 400);
              return;
            }
          }
          goToMid(logicalIndex + 1, true);
        };
        const goPrev = (e) => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
            if (e.stopImmediatePropagation) e.stopImmediatePropagation();
          }
          if (logicalIndex === 0) {
            const beforeLast = track.querySelector(
              `.product-gallery__media[data-suhi-qv-set="before"][data-suhi-qv-gidx="${count - 1}"]`
            );
            if (beforeLast) {
              normalizing = false;
              track.scrollTo({ left: beforeLast.offsetLeft, behavior: 'smooth' });
              setTimeout(onSettle, 400);
              return;
            }
          }
          goToMid(logicalIndex - 1, true);
        };

        root.querySelectorAll('carousel-next-button button, carousel-next-button').forEach((btn) => {
          btn.addEventListener('click', goNext, true);
          btn.removeAttribute('disabled');
          btn.disabled = false;
        });
        root.querySelectorAll('carousel-prev-button button, carousel-prev-button').forEach((btn) => {
          btn.addEventListener('click', goPrev, true);
          btn.removeAttribute('disabled');
          btn.disabled = false;
        });

        const unlockArrows = () => {
          root.querySelectorAll('carousel-prev-button button, carousel-next-button button').forEach((b) => {
            b.removeAttribute('disabled');
            b.disabled = false;
            b.setAttribute('aria-disabled', 'false');
          });
        };
        track.addEventListener('scroll', unlockArrows, { passive: true });
        unlockArrows();
        setTimeout(unlockArrows, 300);
      }

      /* Expose for settle after open */
      track.__suhiQvGoToMid = goToMid;
    };

    const initQuickBuyLoops = () => {
      document
        .querySelectorAll(
          'quick-buy-modal[open] .product-gallery__carousel, quick-buy-modal .product-gallery__carousel, .quick-buy-modal__gallery-wrapper .product-gallery__carousel'
        )
        .forEach((track) => {
          /* Content may still be empty on first open tick */
          const n = track.querySelectorAll(
            ':scope > .product-gallery__media:not([hidden]):not([data-suhi-qv-clone])'
          ).length;
          if (n < 2) return;
          wireQvLoop(track);
        });
    };

    const obs = new MutationObserver(() => {
      initQuickBuyLoops();
    });
    const startObs = () => {
      document.querySelectorAll('quick-buy-modal').forEach((modal) => {
        if (modal.dataset.suhiQvObs) return;
        modal.dataset.suhiQvObs = '1';
        obs.observe(modal, {
          attributes: true,
          attributeFilter: ['open', 'hidden'],
          childList: true,
          subtree: true
        });
      });
      initQuickBuyLoops();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startObs);
    } else {
      startObs();
    }
    document.addEventListener('shopify:section:load', startObs);
    document.addEventListener('product:rerender', () => setTimeout(initQuickBuyLoops, 50));
    document.addEventListener(
      'click',
      (e) => {
        if (
          !e.target.closest?.(
            '.product-card__quick-add, [aria-controls*="quick"], product-card, .product-card__quickview-button'
          )
        )
          return;
        setTimeout(initQuickBuyLoops, 100);
        setTimeout(initQuickBuyLoops, 400);
        setTimeout(initQuickBuyLoops, 900);
      },
      true
    );
  })();

  document.addEventListener('shopify:section:load', initUspRotators);
  document.addEventListener('product:rerender', initUspRotators);

  /* Fix double-tap zoom on product card controls (iOS) */
  document.addEventListener('touchend', (e) => {
    const t = e.target.closest(
      '.wishlist-button__btn, .product-card__quickview-button, .product-card__size, .cart-tabs__btn, .suhi-xtra__nav button'
    );
    if (!t) return;
    /* Allow default once; prevent synthetic double-tap zoom delay via touch-action CSS */
  }, { passive: true });

  /*
    Reels pause/play is owned by sections/shoppable-reels.liquid.
    On PRODUCT pages, limit concurrent autoplay (desktop lag: many muted videos).
  */
  const wireReels = () => {
    const isPdp = document.body && document.body.classList.contains('template-product');
    document.querySelectorAll('.suhi-reels__media video').forEach((v) => {
      if (v.dataset.suhiIo) return;
      v.dataset.suhiIo = '1';
      if (v.preload === 'auto' || isPdp) v.preload = 'none';
    });
    if (!isPdp) return;
    /* Cap: only 1 carousel reel playing at a time on PDP */
    const medias = Array.from(document.querySelectorAll('[id^="suhi-reels-"] .suhi-reels__media[data-reel]'));
    if (!medias.length || medias[0].dataset.suhiPdpCap) return;
    medias.forEach((m) => { m.dataset.suhiPdpCap = '1'; });
    let active = null;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const m = e.target;
          const v = m.querySelector('video');
          if (!v) return;
          if (e.isIntersecting && e.intersectionRatio >= 0.45) {
            if (active && active !== m) {
              const ov = active.querySelector('video');
              if (ov) {
                try { ov.pause(); } catch (_) {}
              }
            }
            active = m;
          } else if (active === m) {
            try { v.pause(); } catch (_) {}
          }
        });
      },
      { threshold: [0, 0.45] }
    );
    medias.forEach((m) => io.observe(m));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wireReels);
  else wireReels();
  document.addEventListener('shopify:section:load', wireReels);

  /* 9) Squad auto-loop slideshow — skip entirely on product pages.
     CRITICAL: pause while Shop the Look popover / Choose Options is open.
     Otherwise the next-look tick (often 1–2s later if you open mid-cycle)
     advances the carousel and the size/product popup closes itself. */
  const loopSquad = () => {
    if (document.body && document.body.classList.contains('template-product')) return;
    document.querySelectorAll('[data-suhi-squad-loop]').forEach((sectionRoot) => {
      if (sectionRoot.dataset.loopInit) return;
      sectionRoot.dataset.loopInit = '1';
      const ms = parseInt(sectionRoot.getAttribute('data-suhi-squad-loop') || '4500', 10);
      const nextBtn = sectionRoot.querySelector('carousel-next-button button, carousel-next-button');
      if (!nextBtn) return;
      let inView = false;
      const popupOpen = () =>
        !!(
          document.querySelector(
            'shop-the-look-popover[open], .shop-the-look__popover[open], shop-the-look-mobile-carousel.is-expanded, quick-buy-modal[open], .quick-buy-modal[open]'
          ) || window.__suhiSizeChartOpen
        );
      const tick = () => {
        if (document.hidden || !inView) return;
        if (popupOpen()) return; /* keep popup open — do not advance looks */
        try {
          nextBtn.click();
        } catch (_) {}
      };
      const timer = setInterval(tick, Math.max(2500, ms));
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            inView = !!(entries[0] && entries[0].isIntersecting);
          },
          { threshold: 0.2 }
        );
        io.observe(sectionRoot);
      } else {
        inView = true;
      }
      sectionRoot._suhiSquadTimer = timer;
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', loopSquad);
  else loopSquad();
  document.addEventListener('shopify:section:load', loopSquad);

  /* ======================================================================
     Shop the Look / Suhi Squad mobile: keep popover + nested Choose Options
     open. Nested focus traps + overlay clicks were dismissing the size popup.
     ====================================================================== */
  (function keepShopTheLookPopupsOpen() {
    if (window.__suhiStlKeepOpen) return;
    window.__suhiStlKeepOpen = true;

    let guardUntil = 0;
    const bump = (ms) => {
      guardUntil = Math.max(guardUntil, Date.now() + (ms || 1200));
    };

    const qbOpen = () => !!document.querySelector('quick-buy-modal[open], .quick-buy-modal[open]');
    const stlPopoverOpen = () =>
      !!document.querySelector('shop-the-look-popover[open], .shop-the-look__popover[open]');

    /* While Choose Options is open from a look card, don't let the look popover die */
    const armPopover = (popover) => {
      if (!popover || popover.__suhiStlArmed) return;
      popover.__suhiStlArmed = true;
      const origHide = typeof popover.hide === 'function' ? popover.hide.bind(popover) : null;
      if (origHide) {
        popover.hide = function () {
          if (qbOpen() || Date.now() < guardUntil) {
            return Promise.resolve();
          }
          return origHide.apply(this, arguments);
        };
      }
      const origRemove = popover.removeAttribute.bind(popover);
      popover.removeAttribute = function (name) {
        if (String(name).toLowerCase() === 'open' && (qbOpen() || Date.now() < guardUntil)) {
          return;
        }
        return origRemove(name);
      };
      /* Overlay click on look popover must not close while QB is up */
      const stopOverlayClose = (e) => {
        if (!qbOpen()) return;
        const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
        const onOverlay = path.some(
          (n) => n && n.getAttribute && n.getAttribute('part') === 'overlay'
        );
        if (onOverlay || (e.target && e.target.getAttribute && e.target.getAttribute('part') === 'overlay')) {
          e.stopImmediatePropagation();
          e.preventDefault();
        }
      };
      popover.addEventListener('click', stopOverlayClose, true);
      if (popover.shadowRoot) {
        popover.shadowRoot.addEventListener('click', stopOverlayClose, true);
      }
    };

    const armAllPopovers = () => {
      document
        .querySelectorAll('shop-the-look-popover, .shop-the-look__popover')
        .forEach(armPopover);
    };

    /* Opening look popover or QB — bump guard so mid-cycle auto-loop can't race */
    document.addEventListener(
      'dialog:before-show',
      (e) => {
        const t = e.target;
        if (!t) return;
        if (t.tagName === 'SHOP-THE-LOOK-POPOVER' || t.classList?.contains('shop-the-look__popover')) {
          bump(1500);
          armPopover(t);
        }
        if (t.tagName === 'QUICK-BUY-MODAL') {
          bump(2000);
          armAllPopovers();
        }
      },
      true
    );

    document.addEventListener(
      'click',
      (e) => {
        const hot = e.target.closest?.('.shop-the-look__hot-spot, [aria-controls*="shop-the-look-item-popover"]');
        const plus = e.target.closest?.('.product-card__quick-add, [aria-controls*="product-quick-buy"]');
        if (hot || plus) {
          bump(2000);
          armAllPopovers();
        }
      },
      true
    );

    /* Prototype: ShopTheLookPopover already returns false for outside hide;
       also block hide while nested QB open at prototype level if needed */
    const patchCtor = () => {
      const Ctor = customElements.get('shop-the-look-popover');
      if (!Ctor || Ctor.prototype.__suhiStlProto) return true;
      Ctor.prototype.__suhiStlProto = true;
      const origHide = Ctor.prototype.hide;
      if (typeof origHide === 'function') {
        Ctor.prototype.hide = function () {
          if (qbOpen()) return Promise.resolve();
          return origHide.apply(this, arguments);
        };
      }
      const origOut = Ctor.prototype.hideForOutsideClickTarget;
      if (typeof origOut === 'function') {
        Ctor.prototype.hideForOutsideClickTarget = function (target) {
          if (qbOpen()) return false;
          if (target && target.closest && target.closest('quick-buy-modal')) return false;
          return origOut.call(this, target);
        };
      }
      return true;
    };
    if (!patchCtor()) customElements.whenDefined('shop-the-look-popover').then(patchCtor);

    const boot = () => armAllPopovers();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
    document.addEventListener('shopify:section:load', () => setTimeout(boot, 50));
  })();

  /* ========================================================================
     Universal L↔R product carousel: always-visible arrows + drag scroll
     Covers featured collections, complementary PDP, any .suhi-hscroll
     ======================================================================== */
  const wireHScroll = (root) => {
    if (!root || root.dataset.hscrollBound) return;
    root.dataset.hscrollBound = '1';
    const track =
      root.querySelector('[data-suhi-hscroll-track]') ||
      root.querySelector('scroll-carousel.suhi-hscroll__track') ||
      root.querySelector('scroll-carousel.product-list--carousel') ||
      root.querySelector('.suhi-hscroll__track') ||
      root.querySelector('scroll-carousel');
    if (!track) return;

    const prev =
      root.querySelector('[data-suhi-hscroll-prev]') ||
      root.querySelector('carousel-prev-button button') ||
      root.querySelector('.prev-next-button--prev');
    const next =
      root.querySelector('[data-suhi-hscroll-next]') ||
      root.querySelector('carousel-next-button button') ||
      root.querySelector('.prev-next-button--next');

    const step = () => {
      const card = track.querySelector('.product-card, .horizontal-product-card, .suhi-reels__card, [role="listitem"]');
      if (card) {
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 16;
        return Math.round(card.getBoundingClientRect().width + gap);
      }
      return Math.round(track.clientWidth * 0.75);
    };
    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      const can = track.scrollWidth > track.clientWidth + 4;
      if (prev) {
        prev.disabled = track.scrollLeft <= 2;
        prev.style.display = can ? '' : 'none';
      }
      if (next) {
        next.disabled = track.scrollLeft >= max;
        next.style.display = can ? '' : 'none';
      }
    };
    if (prev) {
      prev.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });
    }
    if (next) {
      next.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });
    }
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
    setTimeout(update, 300);
    setTimeout(update, 1200);

    /* Mouse drag on desktop — never steal + / wishlist / size / links (Choose Options) */
    const isCardControl = (el) =>
      !!(
        el &&
        el.closest &&
        el.closest(
          [
            'a',
            'button',
            'input',
            'select',
            'textarea',
            'label',
            'wishlist-button',
            '[aria-controls]',
            '.product-card__quick-add',
            '.product-card__size',
            '.wishlist-button__btn',
            '.product-card__actions',
          ].join(',')
        )
      );

    let dragging = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') return;
      if (e.button != null && e.button !== 0) return;
      /* Landing featured-collections: hscroll was eating + clicks → no Choose Options */
      if (isCardControl(e.target)) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startLeft = track.scrollLeft;
      track.classList.add('is-dragging');
      try { track.setPointerCapture(e.pointerId); } catch (_) {}
    });
    track.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      track.scrollLeft = startLeft - dx;
    });
    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      update();
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener(
      'click',
      (e) => {
        if (!moved) return;
        /* Always allow + / heart / size / product links even after a tiny drag */
        if (isCardControl(e.target)) {
          moved = false;
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      },
      true
    );
  };

  const initAllHScroll = () => {
    document.querySelectorAll('.suhi-hscroll, .floating-controls-container.suhi-hscroll, .complementary-products__hscroll').forEach(wireHScroll);
    /* Also wire any featured product-list carousel that still uses floating-controls */
    document.querySelectorAll('product-list.floating-controls-container').forEach(wireHScroll);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAllHScroll);
  else initAllHScroll();
  document.addEventListener('shopify:section:load', initAllHScroll);
  /*
    Complementary products load async — watch a short window only.
    Full-body MutationObserver for 15s was a major PDP desktop lag source.
  */
  (() => {
    let mo = null;
    let scheduled = false;
    const kick = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        initAllHScroll();
      });
    };
    const start = () => {
      if (!document.body) return;
      /* Prefer product complementary zone if present; else short body watch */
      const scope =
        document.querySelector('.complementary-products, product-recommendations, #main') ||
        document.body;
      mo = new MutationObserver(kick);
      mo.observe(scope, { childList: true, subtree: true });
      /* Much shorter on PDP; a bit longer on collection/home for AJAX carousels */
      const ms = document.body.classList.contains('template-product') ? 4000 : 8000;
      setTimeout(() => {
        if (mo) {
          mo.disconnect();
          mo = null;
        }
        initAllHScroll();
      }, ms);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
  })();

  /* ========================================================================
     Claude-analysis hard fixes: hero paint + reveal blanks + wishlist init
     ======================================================================== */
  const forceHeroPaint = () => {
    document.querySelectorAll('slideshow-carousel.slideshow, .shopify-section--slideshow slideshow-carousel').forEach((el) => {
      el.style.background = el.style.background || '#f5f0eb';
      const slides = el.querySelectorAll('.slideshow__slide');
      slides.forEach((s) => {
        const active = s.classList.contains('is-selected');
        if (active) {
          /* Only force the active slide — never first-child (breaks multi-slide autoplay) */
          s.style.opacity = '1';
          s.style.visibility = 'visible';
          s.removeAttribute('reveal-on-scroll');
          s.querySelectorAll('img').forEach((img) => {
            img.style.opacity = '1';
            img.style.visibility = 'visible';
            if (!img.complete) {
              img.addEventListener('load', () => {
                img.style.opacity = '1';
                img.style.visibility = 'visible';
              }, { once: true });
            }
          });
        } else {
          /* Clear paint hacks so theme can hide + animate non-selected slides */
          s.style.removeProperty('opacity');
          s.style.removeProperty('visibility');
          s.querySelectorAll('img').forEach((img) => {
            img.style.removeProperty('opacity');
            img.style.removeProperty('visibility');
          });
        }
      });
    });
  };

  /* Keep forceHeroPaint in sync when carousel advances (autoplay / dots / swipe) */
  document.addEventListener(
    'carousel:settle',
    (e) => {
      if (e.target && e.target.matches && e.target.matches('slideshow-carousel, slideshow-carousel.slideshow')) {
        forceHeroPaint();
      }
    },
    true
  );

  const forceRevealVisible = () => {
    document.querySelectorAll('[reveal-on-scroll="true"]').forEach((el) => {
      el.style.opacity = '1';
      el.style.visibility = 'visible';
      /* Keep attribute for theme animation if it runs, but never stay at CSS opacity 0 */
    });
  };

  const syncWishlistHearts = () => {
    document.querySelectorAll('wishlist-button').forEach((btn) => {
      if (typeof btn.reflect === 'function') btn.reflect();
      else {
        const handle = btn.getAttribute('handle');
        if (!handle) {
          btn.classList.remove('is-active');
          return;
        }
        try {
          const on = (JSON.parse(localStorage.getItem('theme:wishlist') || '[]') || []).some(
            (x) => (x.handle || x) === handle
          );
          btn.classList.toggle('is-active', !!on);
        } catch (_) {
          btn.classList.remove('is-active');
        }
      }
    });
  };

  const runClaudeFixes = () => {
    forceHeroPaint();
    forceRevealVisible();
    syncWishlistHearts();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runClaudeFixes);
  } else {
    runClaudeFixes();
  }
  window.addEventListener('load', runClaudeFixes, { once: true });
  /* Safety: if anything still blank after settle, force paint without needing resize */
  setTimeout(runClaudeFixes, 800);
  setTimeout(runClaudeFixes, 2500);
  document.addEventListener('shopify:section:load', runClaudeFixes);

  /* ======================================================================
     CART: prevent white flash on remove
     Theme fades drawer body to opacity 0 when cart hits 0 items (white flash).
     Also catch /cart/change remove links that are outside line-item-quantity.
     ====================================================================== */
  (function patchCartRemoveFlash() {
    const softReplace = () => {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || drawer.__suhiFlashPatched) return;
      drawer.__suhiFlashPatched = true;

      drawer.addEventListener('click', async (e) => {
        const a = e.target.closest('a[href*="/cart/change"]');
        if (!a) return;
        /* Theme line-item-quantity already handles nested remove links */
        if (a.closest('line-item-quantity') && a.closest('line-item-quantity').tagName) {
          /* still let native handler run — do not double-fetch unless default wasn't prevented */
        }
      }, true);

      const keepBodyOpaque = () => {
        try {
          const body = drawer.shadowRoot && drawer.shadowRoot.querySelector('[part="body"]');
          if (!body) return;
          const op = parseFloat(getComputedStyle(body).opacity);
          if (op < 0.99) {
            body.style.opacity = '1';
            body.style.transform = 'none';
          }
        } catch (_) {}
      };

      const obs = new MutationObserver(keepBodyOpaque);
      const attach = () => {
        if (!drawer.shadowRoot) return false;
        obs.observe(drawer.shadowRoot, {
          attributes: true,
          subtree: true,
          attributeFilter: ['style', 'class']
        });
        keepBodyOpaque();
        return true;
      };
      if (!attach()) {
        const t = setInterval(() => {
          if (attach()) clearInterval(t);
        }, 150);
        setTimeout(() => clearInterval(t), 6000);
      }
      document.addEventListener('cart:change', () => {
        requestAnimationFrame(keepBodyOpaque);
        setTimeout(keepBodyOpaque, 50);
        setTimeout(keepBodyOpaque, 200);
        setTimeout(keepBodyOpaque, 400);
      });
    };

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', softReplace);
    else softReplace();
    document.addEventListener('shopify:section:load', softReplace);
  })();

  /* ======================================================================
     CART DRAWER: stay open across cart:refresh / content replace
     FocusTrap + shadow overlay often call hide() right after replaceChildren
     (discount, qty, xtra, login sync). Hold open until the user closes it.
     ====================================================================== */
  (function keepCartDrawerOpenOnRefresh() {
    if (window.__suhiCartDrawerKeepOpen) return;
    window.__suhiCartDrawerKeepOpen = true;

    let holdOpen = false; /* user has the drawer open — keep it that way */
    let refreshGuardUntil = 0;
    let userClosing = false;

    const getDrawer = () => document.querySelector('cart-drawer');
    const isDrawerOpen = (d) => !!(d && (d.open || d.hasAttribute('open')));
    const markGuard = (ms) => {
      refreshGuardUntil = Date.now() + (ms || 2000);
    };
    const inGuard = () => Date.now() < refreshGuardUntil;

    const ensureOpen = (drawer) => {
      if (!drawer || !holdOpen || userClosing) return;
      if (!drawer.hasAttribute('open')) {
        try {
          /* Prefer show() so FocusTrap re-activates cleanly */
          if (typeof drawer.show === 'function') drawer.show(false);
          else drawer.setAttribute('open', 'immediate');
        } catch (_) {
          try {
            drawer.setAttribute('open', '');
          } catch (__) {}
        }
      }
    };

    const armDrawer = (drawer) => {
      if (!drawer || drawer.__suhiKeepOpenArmed) return;
      drawer.__suhiKeepOpenArmed = true;

      const origHide = typeof drawer.hide === 'function' ? drawer.hide.bind(drawer) : null;
      if (origHide) {
        drawer.hide = function () {
          /* Only X / overlay / Esc set userClosing — block FocusTrap auto-closes */
          if (holdOpen && !userClosing) {
            return Promise.resolve();
          }
          holdOpen = false;
          return origHide.apply(this, arguments);
        };
      }

      const origRemove = drawer.removeAttribute.bind(drawer);
      drawer.removeAttribute = function (name) {
        if (String(name).toLowerCase() === 'open' && holdOpen && !userClosing) {
          return; /* never strip open while user session wants it open */
        }
        return origRemove(name);
      };

      /* Mark intentional close from X / overlay / Esc */
      const markUserClose = () => {
        userClosing = true;
        holdOpen = false;
        setTimeout(() => {
          userClosing = false;
        }, 400);
      };

      drawer.addEventListener(
        'click',
        (e) => {
          const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
          const hit = path.find(
            (n) =>
              n &&
              n.getAttribute &&
              (n.getAttribute('part') === 'overlay' ||
                n.getAttribute('part') === 'close-button' ||
                n.tagName === 'DIALOG-CLOSE-BUTTON')
          );
          if (hit) markUserClose();
        },
        true
      );

      const bindOverlay = () => {
        try {
          const overlay = drawer.shadowRoot && drawer.shadowRoot.querySelector('[part="overlay"]');
          if (!overlay || overlay.__suhiHoldBound) return !!overlay;
          overlay.__suhiHoldBound = true;
          /* Mark as user intent BEFORE Prestige's overlay hide() listener runs */
          overlay.addEventListener('click', () => markUserClose(), true);
          overlay.addEventListener('pointerdown', () => markUserClose(), true);
          return true;
        } catch (_) {
          return false;
        }
      };
      if (!bindOverlay()) {
        const t = setInterval(() => {
          if (bindOverlay()) clearInterval(t);
        }, 100);
        setTimeout(() => clearInterval(t), 5000);
      }

      drawer.addEventListener('dialog:after-show', () => {
        holdOpen = true;
        userClosing = false;
        markGuard(500); /* brief guard right after open */
      });
      drawer.addEventListener('dialog:after-hide', () => {
        if (userClosing || !inGuard()) {
          holdOpen = false;
        } else if (holdOpen) {
          /* Was forced shut during guard — reopen */
          requestAnimationFrame(() => ensureOpen(drawer));
          setTimeout(() => ensureOpen(drawer), 50);
        }
      });
      drawer.addEventListener('cart-drawer:refreshed', () => {
        if (!holdOpen) return;
        markGuard(2000);
        ensureOpen(drawer);
        requestAnimationFrame(() => ensureOpen(drawer));
        setTimeout(() => ensureOpen(drawer), 50);
        setTimeout(() => ensureOpen(drawer), 200);
        setTimeout(() => ensureOpen(drawer), 500);
      });

      /* If something strips [open] while we hold it, put it back */
      const mo = new MutationObserver(() => {
        if (holdOpen && !userClosing && !drawer.hasAttribute('open')) {
          ensureOpen(drawer);
        }
      });
      mo.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    };

    const onCartSignal = () => {
      const drawer = getDrawer();
      if (!drawer) return;
      armDrawer(drawer);
      if (isDrawerOpen(drawer) || holdOpen) {
        holdOpen = true;
        markGuard(2000);
      }
    };

    document.addEventListener('cart:refresh', onCartSignal, true);
    document.addEventListener('cart:change', onCartSignal, true);

    const patchProto = () => {
      const Ctor = customElements.get('cart-drawer');
      if (!Ctor || Ctor.prototype.__suhiOutsidePatch) return true;
      Ctor.prototype.__suhiOutsidePatch = true;
      if (typeof Ctor.prototype.hideForOutsideClickTarget === 'function') {
        const orig = Ctor.prototype.hideForOutsideClickTarget;
        Ctor.prototype.hideForOutsideClickTarget = function (target) {
          if (holdOpen && !userClosing) return false;
          return orig.call(this, target);
        };
      }
      return true;
    };
    if (!patchProto()) customElements.whenDefined('cart-drawer').then(patchProto);

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape') return;
        const drawer = getDrawer();
        if (drawer && isDrawerOpen(drawer)) {
          userClosing = true;
          holdOpen = false;
          setTimeout(() => {
            userClosing = false;
          }, 400);
        }
      },
      true
    );

    const boot = () => armDrawer(getDrawer());
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
    document.addEventListener('shopify:section:load', boot);
  })();

  /* ======================================================================
     CHOOSE OPTIONS: keep open across product:rerender / gallery settles
     Same FocusTrap race as cart — variant change or carousel loop rewrite
     can look like an outside click and dump the shopper back to the grid.
     ====================================================================== */
  (function keepQuickBuyOpenOnRerender() {
    if (window.__suhiQbKeepOpenRerender) return;
    window.__suhiQbKeepOpenRerender = true;

    let guardUntil = 0;
    const bump = (ms) => {
      guardUntil = Math.max(guardUntil, Date.now() + (ms || 700));
    };

    document.addEventListener('product:rerender', () => bump(900), true);
    document.addEventListener(
      'variant:change',
      () => {
        if (document.querySelector('quick-buy-modal[open]')) bump(900);
      },
      true
    );

    const patch = () => {
      const Ctor = customElements.get('quick-buy-modal');
      if (!Ctor || Ctor.prototype.__suhiRerenderGuard) return true;
      Ctor.prototype.__suhiRerenderGuard = true;

      let hideProto = Ctor.prototype;
      while (hideProto && hideProto !== HTMLElement.prototype) {
        if (Object.prototype.hasOwnProperty.call(hideProto, 'hide')) {
          const origHide = hideProto.hide;
          hideProto.hide = function () {
            if (this.tagName === 'QUICK-BUY-MODAL' && Date.now() < guardUntil) {
              return Promise.resolve();
            }
            /* Size-chart protection still lives in suhiSizeChartKeepQuickBuy */
            if (this.tagName === 'QUICK-BUY-MODAL' && window.__suhiSizeChartOpen) {
              return Promise.resolve();
            }
            return origHide.apply(this, arguments);
          };
          break;
        }
        hideProto = Object.getPrototypeOf(hideProto);
      }

      if (typeof Ctor.prototype.hideForOutsideClickTarget === 'function') {
        const origOut = Ctor.prototype.hideForOutsideClickTarget;
        Ctor.prototype.hideForOutsideClickTarget = function (target) {
          if (this.tagName === 'QUICK-BUY-MODAL') {
            if (Date.now() < guardUntil) return false;
            if (window.__suhiSizeChartOpen) return false;
          }
          return origOut.call(this, target);
        };
      }
      return true;
    };

    if (!patch()) customElements.whenDefined('quick-buy-modal').then(patch);
  })();

  /* ======================================================================
     SIZE CHART: open image overlay; keep Choose Options (quick-buy) open
     Root causes fixed:
     1) Script used to live inside <template id="quick-buy-content"> and never
        ran when Choose Options was loaded via AJAX on the landing page.
     2) Prestige FocusTrap treats the chart (outside quick-buy) as an outside
        click and calls hide(), dumping the shopper back on the page behind.
     ====================================================================== */
  (function suhiSizeChartKeepQuickBuy() {
    if (window.__suhiSizeChartReady) return;
    window.__suhiSizeChartReady = true;
    window.__suhiSizeChartOpen = false;

    let blockQbHideUntil = 0;
    let overlay = null;
    let imgEl = null;
    let titleEl = null;
    const armed = new WeakSet();

    const getOpenQuickBuy = () => {
      const all = document.querySelectorAll('quick-buy-modal');
      for (let i = 0; i < all.length; i++) {
        if (all[i].open || all[i].hasAttribute('open')) return all[i];
      }
      return null;
    };

    /* Walk light + shadow composed path (Prestige modals use open shadow roots). */
    const composedContainsOverlay = (target) => {
      let node = target;
      while (node) {
        if (node.id === 'suhi-sizechart-overlay') return true;
        if (node.tagName === 'X-MODAL' || node.tagName === 'X-DRAWER') return true;
        if (node.parentNode) node = node.parentNode;
        else if (node.host) node = node.host;
        else node = null;
      }
      return false;
    };

    const otherOverlayOpen = (qb) => {
      if (window.__suhiSizeChartOpen) return true;
      if (Date.now() < blockQbHideUntil) return true;
      const nested = document.querySelectorAll('x-modal[open], x-drawer[open]');
      for (let i = 0; i < nested.length; i++) {
        const m = nested[i];
        if (qb && (m === qb || qb.contains(m))) continue;
        return true;
      }
      return false;
    };

    const shouldProtect = (el) => {
      if (!el || el.tagName !== 'QUICK-BUY-MODAL') return false;
      return otherOverlayOpen(el);
    };

    /* Nuclear: block removeAttribute('open') on the open quick-buy while chart/nested modal is up.
       Works even when FocusTrap bound the original hide() reference. */
    const armQuickBuy = (qb) => {
      if (!qb || armed.has(qb)) return;
      armed.add(qb);
      const origRemove = qb.removeAttribute.bind(qb);
      qb.removeAttribute = function (name) {
        if (String(name).toLowerCase() === 'open' && shouldProtect(this)) {
          return;
        }
        return origRemove(name);
      };
    };

    const armOpenQuickBuy = () => {
      const qb = getOpenQuickBuy();
      if (qb) armQuickBuy(qb);
      return qb;
    };

    /* Prototype-level hide + outside-click guards (belt + suspenders) */
    const patchProtos = () => {
      if (window.__suhiQbHidePatched) return true;
      const Ctor = customElements.get('quick-buy-modal');
      if (!Ctor) return false;
      window.__suhiQbHidePatched = true;

      let hideProto = Ctor.prototype;
      while (hideProto && hideProto !== HTMLElement.prototype) {
        if (Object.prototype.hasOwnProperty.call(hideProto, 'hide')) {
          const origHide = hideProto.hide;
          hideProto.hide = function () {
            if (shouldProtect(this)) return Promise.resolve();
            return origHide.apply(this, arguments);
          };
          break;
        }
        hideProto = Object.getPrototypeOf(hideProto);
      }

      let outProto = Ctor.prototype;
      while (outProto && outProto !== HTMLElement.prototype) {
        if (Object.prototype.hasOwnProperty.call(outProto, 'hideForOutsideClickTarget')) {
          const origOut = outProto.hideForOutsideClickTarget;
          outProto.hideForOutsideClickTarget = function (target) {
            if (this.tagName === 'QUICK-BUY-MODAL') {
              if (window.__suhiSizeChartOpen) return false;
              if (Date.now() < blockQbHideUntil) return false;
              if (target && composedContainsOverlay(target)) return false;
            }
            return origOut.call(this, target);
          };
          break;
        }
        outProto = Object.getPrototypeOf(outProto);
      }
      return true;
    };

    if (!patchProtos()) {
      customElements.whenDefined('quick-buy-modal').then(patchProtos);
    }

    const ensureOverlay = () => {
      if (overlay) return;
      overlay = document.createElement('div');
      overlay.id = 'suhi-sizechart-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.innerHTML =
        '<div class="suhi-sc__backdrop" data-suhi-sc-close></div>' +
        '<div class="suhi-sc__panel" role="dialog" aria-modal="true" aria-label="Size chart">' +
        '<div class="suhi-sc__head"><span class="suhi-sc__title"></span>' +
        '<button type="button" class="suhi-sc__close" data-suhi-sc-close aria-label="Close">&times;</button></div>' +
        '<div class="suhi-sc__body"><img alt="Size chart"></div></div>';
      document.body.appendChild(overlay);
      imgEl = overlay.querySelector('img');
      titleEl = overlay.querySelector('.suhi-sc__title');

      /*
        Single delegated handler. Previous version attached stopImmediatePropagation on the
        overlay capture phase, which ran BEFORE the X/backdrop listeners and blocked close.
      */
      const onOverlayPointer = (e) => {
        if (!overlay.classList.contains('is-open')) return;
        const closeEl = e.target.closest('[data-suhi-sc-close]');
        if (closeEl || e.target === overlay) {
          e.preventDefault();
          e.stopPropagation();
          if (e.stopImmediatePropagation) e.stopImmediatePropagation();
          closeChart();
          return;
        }
        /* Keep panel interactions from leaking to quick-buy FocusTrap underneath */
        if (e.target.closest('.suhi-sc__panel')) {
          e.stopPropagation();
        }
      };

      /* pointerdown closes immediately (before FocusTrap mouse handlers); click as backup */
      ['pointerdown', 'click'].forEach((evt) => {
        overlay.addEventListener(evt, onOverlayPointer, true);
      });

      if (!document.getElementById('suhi-sizechart-style')) {
        const style = document.createElement('style');
        style.id = 'suhi-sizechart-style';
        style.textContent =
          '#suhi-sizechart-overlay{position:fixed;inset:0;z-index:2147483000;display:none;align-items:center;justify-content:center;padding:16px;}' +
          '#suhi-sizechart-overlay.is-open{display:flex;}' +
          '#suhi-sizechart-overlay .suhi-sc__backdrop{position:absolute;inset:0;background:rgba(0,0,0,.7);cursor:pointer;}' +
          /* Contained modal — not full-screen; keeps chart in a readable ratio */
          '#suhi-sizechart-overlay .suhi-sc__panel{position:relative;z-index:1;background:#fff;border-radius:10px;' +
          'width:min(480px,92vw);max-width:480px;max-height:min(82vh,720px);' +
          'overflow:hidden;display:flex;flex-direction:column;box-shadow:0 12px 40px rgba(0,0,0,.28);}' +
          '#suhi-sizechart-overlay .suhi-sc__head{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-bottom:1px solid #eee;flex:0 0 auto;}' +
          '#suhi-sizechart-overlay .suhi-sc__title{font-size:15px;font-weight:600;}' +
          '#suhi-sizechart-overlay .suhi-sc__close{background:none;border:none;font-size:26px;line-height:1;cursor:pointer;color:#333;padding:8px;margin:-8px;min-width:40px;min-height:40px;display:inline-flex;align-items:center;justify-content:center;position:relative;z-index:2;}' +
          '#suhi-sizechart-overlay .suhi-sc__body{overflow:auto;padding:12px 14px 16px;flex:1 1 auto;min-height:0;' +
          'display:flex;align-items:center;justify-content:center;}' +
          '#suhi-sizechart-overlay .suhi-sc__body img{display:block;width:100%;max-width:100%;height:auto;max-height:min(68vh,560px);object-fit:contain;margin:0 auto;}' +
          /* Desktop: slightly larger but still contained (not edge-to-edge) */
          '@media (min-width:1000px){' +
          '#suhi-sizechart-overlay{padding:32px;}' +
          '#suhi-sizechart-overlay .suhi-sc__panel{width:min(520px,42vw);max-width:520px;max-height:min(78vh,680px);}' +
          '#suhi-sizechart-overlay .suhi-sc__body{padding:16px 18px 20px;}' +
          '#suhi-sizechart-overlay .suhi-sc__body img{max-height:min(62vh,520px);}' +
          '}' +
          '@media (max-width:699px){' +
          '#suhi-sizechart-overlay .suhi-sc__panel{width:min(100%,420px);max-width:100%;max-height:85vh;}' +
          '#suhi-sizechart-overlay .suhi-sc__body img{max-height:min(70vh,480px);}' +
          '}';
        document.head.appendChild(style);
      }
    };

    const openChart = (src, title) => {
      if (!src) return;
      ensureOverlay();
      patchProtos();
      armOpenQuickBuy();
      imgEl.src = src;
      titleEl.textContent = title || 'Size chart';
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      window.__suhiSizeChartOpen = true;
      blockQbHideUntil = Date.now() + 1200;
    };

    const closeChart = () => {
      /* Always dismiss UI even if flag got out of sync */
      blockQbHideUntil = Date.now() + 800;
      window.__suhiSizeChartOpen = false;
      if (overlay) {
        overlay.classList.remove('is-open');
        overlay.setAttribute('aria-hidden', 'true');
      }
      if (imgEl) imgEl.removeAttribute('src');

      const qb = getOpenQuickBuy();
      if (qb) {
        armQuickBuy(qb);
        /* If hide slipped through, restore open without re-fetching */
        if (!qb.hasAttribute('open') && qb.childNodes.length) {
          try {
            qb.setAttribute('open', '');
          } catch (_) {}
        }
      }
    };

    /* Open image size chart (metafield buttons in variant-picker) */
    document.addEventListener(
      'click',
      (e) => {
        const btn = e.target && e.target.closest && e.target.closest('[data-suhi-sizechart-src]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        openChart(
          btn.getAttribute('data-suhi-sizechart-src'),
          btn.getAttribute('data-suhi-sizechart-title') || 'Size chart'
        );
      },
      true
    );

    /* Theme page-based size chart (x-modal via aria-controls) — arm QB while nested modal is open */
    document.addEventListener(
      'click',
      (e) => {
        const btn = e.target && e.target.closest && e.target.closest('[aria-controls]');
        if (!btn) return;
        const id = btn.getAttribute('aria-controls');
        if (!id) return;
        const target = document.getElementById(id);
        if (!target || (target.tagName !== 'X-MODAL' && !target.classList.contains('modal'))) return;
        const qb = getOpenQuickBuy();
        if (!qb) return;
        armQuickBuy(qb);
        blockQbHideUntil = Date.now() + 1200;
        /* After nested modal opens, keep blocking outside-close of QB */
        setTimeout(() => {
          if (target.open || target.hasAttribute('open')) {
            blockQbHideUntil = Date.now() + 60000;
            armQuickBuy(qb);
          }
        }, 50);
      },
      true
    );

    document.addEventListener(
      'dialog:after-hide',
      (e) => {
        const t = e.target;
        if (!t || (t.tagName !== 'X-MODAL' && t.tagName !== 'X-DRAWER')) return;
        if (t.tagName === 'QUICK-BUY-MODAL') return;
        blockQbHideUntil = Date.now() + 800;
        const qb = getOpenQuickBuy();
        if (qb && !qb.hasAttribute('open') && qb.childNodes.length) {
          try {
            qb.setAttribute('open', '');
          } catch (_) {}
        }
      },
      true
    );

    const chartIsVisible = () =>
      !!(window.__suhiSizeChartOpen || (overlay && overlay.classList.contains('is-open')));

    /* Document capture fallback — runs before nested handlers; guarantees X/backdrop close */
    document.addEventListener(
      'pointerdown',
      (e) => {
        if (!chartIsVisible()) return;
        const t = e.target;
        if (!t) return;
        if (
          (t.closest && t.closest('#suhi-sizechart-overlay [data-suhi-sc-close]')) ||
          (overlay && t === overlay) ||
          (t.classList && t.classList.contains('suhi-sc__backdrop'))
        ) {
          e.preventDefault();
          e.stopPropagation();
          if (e.stopImmediatePropagation) e.stopImmediatePropagation();
          closeChart();
        }
      },
      true
    );

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape') return;
        if (!chartIsVisible()) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        closeChart();
      },
      true
    );

    /* Arm protection whenever Choose Options opens */
    document.addEventListener(
      'dialog:after-show',
      (e) => {
        if (e.target && e.target.tagName === 'QUICK-BUY-MODAL') {
          armQuickBuy(e.target);
          patchProtos();
        }
      },
      true
    );
  })();
})();
